import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuthToken } from '../utils/auth';
import './AjudaWidget.css';
import BASE_URL from '../utils/api';
import TextoFormatadoIA from './TextoFormatadoIA';

// Mapa de rotas para nomes amigáveis das páginas
const routeNames = {
  '/personalizar-contrato': 'Personalizar Contrato',
  '/extrato': 'Extrato',
  '/sidebar/Home': 'Início',
  '/sidebar/ForumEmpresas': 'Fórum Empresas',
  '/sidebar/Cliente': 'Cliente',
  '/sidebar/ClientesDropdown': 'Clientes',
  '/sidebar/FornecedorDropdown': 'Fornecedores',
  '/sidebar/AgendamentosDropdown': 'Agendamentos',
  '/sidebar/CalendarioDropdown': 'Calendário',
  '/sidebar/ContasPagarDropdown': 'Contas a Pagar',
  '/sidebar/ContasReceberDropdown': 'Contas a Receber',
  '/sidebar/EntregasProdutosDropdown': 'Entregas de Produtos',
  '/sidebar/EntregaServicosDropdown': 'Entregas de Serviços',
  '/sidebar/PrazosDropdown': 'Prazos',
  '/sidebar/GarantiaDropdown': 'Garantias',
  '/sidebar/ManutencaoDropdown': 'Manutenção',
  '/sidebar/NotaFiscalDropdown': 'Notas Fiscais',
  '/sidebar/CertificadoPlugNotasDropdown': 'Certificado PlugNotas',
  '/sidebar/GrupoDropDown': 'Grupos',
  '/sidebar/MeusGruposDropdown': 'Meus Grupos',
  '/sidebar/OportunidadeDropdown': 'Oportunidades',
  '/sidebar/OrcamentoDropdown': 'Orçamentos',
  '/sidebar/ProducoesDropdown': 'Projetos',
  '/sidebar/ProjetoVisualDropdown': 'Projeto Visual',
  '/sidebar/TarefasProjetoDropdown': 'Tarefas do Projeto',
  '/sidebar/RelacionamentoClientesDropdown': 'Relacionamento com Clientes',
  '/sidebar/ServicosTerceirosDropdown': 'Serviços de Terceiros',
  '/sidebar/VendasDropdown': 'Vendas',
  '/sidebar/VendasProdutosDropdown': 'Vendas de Produtos',
  '/sidebar/ProdutoDropdown': 'Produtos',
  '/sidebar/EstoqueMaterialDropdown': 'Estoque de Materiais',
  '/sidebar/MateriaPrimaDropdown': 'Matéria-Prima',
  '/sidebar/ResumoResponsaveisDropdown': 'Responsáveis por Projeto',
  '/sidebar/ResumoServicosTerceirosDropdown': 'Serviços Terceiros por Projeto',
  '/sidebar/ResponsaveisDropdown': 'Pessoas',
  '/sidebar/MinhaConta': 'Minha Conta',
  '/sidebar/Configuracoes': 'Configurações',
  '/sidebar/Planos': 'Planos',
  '/sidebar/AlterarSenha': 'Alterar Senha',
  '/sidebar/CadastrarUsuario': 'Cadastrar Funcionários',
  '/sidebar/GerenciarFuncionarios': 'Gerenciar Funcionários',
  '/sidebar/CadastrarConta': 'Conta de Recebimento',
};

// Resumos por página
const pageDescriptions = {
  '/personalizar-contrato': {
    title: 'Personalizar Contrato',
    content: `Aqui você escolhe as cláusulas que vão no PDF dos seus contratos.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Padrão — o contrato usa as cláusulas padrão do sistema\n` +
      `• Personalizado — o contrato padrão completo é carregado para você editar: títulos, textos, "+ Subcláusula" e "+ Adicionar cláusula"\n` +
      `• Arrastar — passe o mouse no container e arraste pelos pontinhos para mudar a ordem; o × remove. A numeração (CLAUSULA PRIMEIRA, 1.1...) se ajusta sozinha\n` +
      `• DAS PARTES — primeira parte fixa, não pode ser editada nem movida\n` +
      `• Cláusula dos serviços — obrigatória: só dá para mudar a posição\n` +
      `• Dados (barra à esquerda) — arraste ou clique para colocar no texto dados do cliente, do orçamento e da sua empresa; no PDF de cada contrato vira o dado real\n` +
      `• Visualizar PDF — mostra como vai ficar, mesmo antes de salvar (os dados saem como __________)\n` +
      `• Salvar — no canto inferior direito; as mudanças só valem depois de salvar\n` +
      `• Restaurar padrão — volta ao padrão e apaga a personalização\n\n` +
      `💡 Contratos já assinados não mudam.\n\n` +
      `💬 Dúvidas? Use o Assistente IA aqui na ajuda.`
  },
  '/extrato': {
    title: 'Extrato',
    content: `Aqui você vê tudo o que entrou no caixa, como um extrato de banco: cada pagamento com a data, a hora e a venda de onde veio.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Período — Este mês, Últimos 30 dias, Todo o período ou escolher as datas\n` +
      `• Vendas aprovadas — total, vendas de produtos e vendas de serviços do período\n` +
      `• Pagamento externo — o que foi pago fora do sistema (dinheiro, Pix direto, maquininha), registrado no botão "💵 Pagamento externo" da venda\n` +
      `• Pagamento interno confirmado — o que o Stripe já confirmou pelo link de pagamento, de vendas de produtos e de serviços (venda ainda não paga não entra)\n` +
      `• Extrato de entradas — agrupado por dia, com o total do dia; cada linha mostra a hora, se foi pagamento externo (azul) ou interno (verde), a forma de pagamento, se foi parcial ou total, a venda de origem e o valor\n\n` +
      `💡 Exemplo: venda de R$ 1.000 com R$ 300 pagos em dinheiro e R$ 700 pelo link aparece como duas entradas separadas, cada uma no seu dia.\n\n` +
      `💬 Dúvidas? Use o Assistente IA aqui na ajuda.`
  },
  '/sidebar/Home': {
    title: 'Página Inicial',
    content: `Esta é a página principal do sistema S6 Gestão.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Feed de publicações — Veja e crie postagens para sua equipe\n` +
      `• Grupos — Acompanhe as atividades dos seus grupos de trabalho\n` +
      `• Atrasos — Painel lateral com itens pendentes e atrasados\n` +
      `• Empresas — Lista de empresas vinculadas à sua conta\n\n` +
      `💡 Dicas:\n` +
      `• Use o campo de postagem para compartilhar atualizações com sua equipe\n` +
      `• Clique em um grupo para ver apenas as postagens dele\n` +
      `• O painel de atrasos mostra tarefas e compromissos que precisam de atenção`
  },
  '/sidebar/ClientesDropdown': {
    title: 'Clientes',
    content: `Gerencie todos os seus clientes nesta tela.\n\n` +
      `📌 Funcionalidades:\n\n` +
      `• Cadastrar novos clientes com dados completos\n` +
      `• Pesquisar clientes pelo nome, CPF/CNPJ ou e-mail\n` +
      `• Editar informações de clientes existentes\n` +
      `• Visualizar histórico de interações e vendas\n\n` +
      `💡 Dicas:\n` +
      `• Use a barra de busca para encontrar rapidamente um cliente\n` +
      `• Mantenha os dados atualizados para relatórios precisos`
  },
  '/sidebar/OrcamentoDropdown': {
    title: 'Orçamentos',
    content: `Aqui você cria e gerencia todos os orçamentos da sua empresa. Um orçamento é uma proposta formal de valor enviada a um cliente, vinculada a uma oportunidade. Ele pode ser aprovado, rejeitado ou ficar pendente.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todos os orçamentos com cliente, oportunidade, valor e status\n` +
      `• Indicador visual de status: 🟢 Verde (Aprovado), 🟠 Laranja (Pendente), 🔴 Vermelho (Rejeitado)\n` +
      `• Data e horário de criação de cada orçamento\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão \"+ Adicionar\" — Inicia o fluxo de criação: escolha o cliente, depois a oportunidade vinculada, e por fim preencha o formulário do orçamento.\n` +
      `• Filtro de Busca — Filtre os orçamentos por: ID do Orçamento, ID do Cliente, Nome do Cliente, ID da Oportunidade ou Status. Use o dropdown laranja para trocar o critério.\n` +
      `• Botão \"Editar\" (em cada card) — Abre o formulário com todos os dados do orçamento para alteração. Ao salvar, o sistema oferece a opção de baixar o PDF atualizado.\n` +
      `• Botão \"Baixar PDF\" (em cada card) — Gera e baixa o PDF do orçamento diretamente.\n` +
      `• Botão \"Compartilhar\" (em cada card) — Abre um popup com opções para copiar o link de aprovação, enviar via WhatsApp ou por e-mail. O cliente recebe o link e pode aprovar ou rejeitar o orçamento.\n\n` +
      `💡 Dicas:\n` +
      `• Compartilhe o link de aprovação com o cliente para que ele possa aprovar ou rejeitar sem precisar acessar o sistema\n` +
      `• Ao aprovar, o status do orçamento muda automaticamente para \"Aprovado\"\n` +
      `• Use o filtro por Status para ver rapidamente todos os orçamentos pendentes`
  },
  '/sidebar/VendasDropdown': {
    title: 'Vendas de Serviços',
    content:
      `Aqui ficam registradas todas as vendas de serviços da sua empresa. Cada venda é gerada normalmente a partir de um orçamento aprovado e fica vinculada ao cliente e ao orçamento correspondente.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todas as vendas de serviços com ID da venda, cliente, orçamento vinculado, CPF/CNPJ, valor final e status\n` +
      `• Indicador visual de cor em cada status para identificação rápida\n` +
      `• Filtro de busca para localizar vendas por Cliente ID, Venda ID, Orçamento ID, Valor ou Status\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Filtro ("Filtrar por" + campo de texto) — Selecione o critério no dropdown e digite o valor para localizar vendas específicas na lista.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário completo da venda para visualização e edição. Dentro do formulário você pode alterar status, desconto, vendedor, tipo e configuração de pagamento, impostos e dados de cartão quando necessário.\n\n` +
      `🎨 Status da Venda:\n\n` +
      `• 🟢 Verde = Aprovada / Concluída\n` +
      `• 🟡 Amarelo = Pendente / Aguardando aprovação\n` +
      `• 🔴 Vermelho = Rejeitada / Cancelada\n` +
      `• 🔵 Azul = Em andamento / Processando\n\n` +
      `💡 Dicas:\n` +
      `• Ao aprovar uma venda (status "Aprovada"), o sistema redireciona automaticamente para Contas a Receber e cria a conta\n` +
      `• Vendas aprovadas ficam bloqueadas para edição — o formulário mostra um aviso em destaque quando isso ocorre\n` +
      `• Use o filtro por Status para ver rapidamente todas as vendas pendentes de aprovação`
  },
  '/sidebar/VendasProdutosDropdown': {
    title: 'Vendas de Produtos',
    content: `Aqui você registra e gerencia todas as vendas de produtos da sua empresa. Cada venda é vinculada a um cliente e a um ou mais produtos do seu catálogo, com controle de quantidade, valor e status.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todas as vendas de produtos com cliente, produto, quantidade e valor\n` +
      `• Status de cada venda com indicador visual de cor\n` +
      `• Data da venda e valor final calculado\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão \"Nova Venda\" — Abre o formulário para registrar uma nova venda. Você seleciona o cliente, escolhe os produtos do catálogo (com controle de estoque), define quantidade e valores.\n` +
      `• Botão \"Importar Produtos\" — Permite importar produtos em lote a partir de um arquivo, agilizando o cadastro de itens em massa.\n` +
      `• Filtro de Busca — Filtre as vendas por: ID da Venda, ID do Cliente, Nome do Cliente, ID do Produto ou Nome do Produto.\n` +
      `• Botão \"Editar\" (em cada card) — Abre a venda para edição com todos os dados e produtos vinculados.\n\n` +
      `🎨 Status da Venda:\n\n` +
      `• 🟢 Verde = Aprovada\n` +
      `• 🟠 Laranja = Pendente\n` +
      `• 🔴 Vermelho = Rejeitada\n\n` +
      `💡 Dicas:\n` +
      `• O sistema verifica o estoque ao selecionar um produto — produtos sem estoque aparecem desabilitados\n` +
      `• O valor final é calculado automaticamente com base nos itens e quantidade`
  },
  '/sidebar/ContratoDropdown': {
    title: 'Contratos',
    content:
      `Aqui você gerencia todos os contratos da sua empresa. Cada contrato é vinculado a um cliente, a um orçamento e a uma venda, reunindo em um só lugar o acordo formalizado com o cliente.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todos os contratos com ID do contrato, nome do cliente, título da oportunidade, ID do cliente, ID da venda, ID do orçamento, valor e status\n` +
      `• Indicador de cor no status para identificação rápida\n` +
      `• Filtro de busca para localizar contratos por ID do Contrato, ID do Cliente, ID do Orçamento, Status ou Valor\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Filtro ("Filtrar por" + campo de texto) — Selecione o critério no dropdown e digite o valor para filtrar a lista.\n` +
      `• Botão "Baixar PDF" (em cada card) — Gera e baixa o PDF do contrato diretamente.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário completo do contrato. Dentro do formulário você pode:\n` +
      `  → Alterar status, valor, data de assinatura, vigência e prazo de garantia\n` +
      `  → Editar descrição e condições de pagamento\n` +
      `  → Fazer upload do PDF do contrato assinado\n` +
      `  → Gerenciar Aditivos Contratuais (só em contrato assinado: criar, editar e remover aditivos de valor, prazo ou escopo)\n\n` +
      `🎨 Status do Contrato:\n\n` +
      `• 🟢 Verde = Assinado\n` +
      `• 🔴 Vermelho = Qualquer outro status (Pendente Assinatura, Em Andamento, Concluído, Cancelado)\n\n` +
      `💡 Dicas:\n` +
      `• Contratos são gerados automaticamente quando uma venda de serviço é aprovada\n` +
      `• Os aditivos contratuais só aparecem depois que o contrato foi assinado e servem para registrar alterações formais de valor, prazo ou escopo sem precisar criar um novo contrato\n` +
      `• Antes da assinatura: corrija o próprio contrato (editar e salvar); o campo de aditivo não aparece\n` +
      `• O PDF assinado é salvo automaticamente no contrato quando o cliente assina pelo link`
  },
  '/sidebar/ContasPagarDropdown': {
    title: 'Contas a Pagar',
    content:
      `Aqui você controla todas as obrigações financeiras da empresa — despesas, compras, pagamentos a fornecedores e qualquer outra saída de dinheiro que precisa ser registrada.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todas as contas a pagar com fornecedor, valor, data de vencimento e status\n` +
      `• Indicador visual de status para identificação rápida de contas pagas, pendentes e vencidas\n` +
      `• Filtro de busca por Fornecedor, Valor, Data de Vencimento ou Status\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Filtro ("Filtrar por" + campo de texto) — Selecione o critério e digite para localizar rapidamente uma conta específica.\n` +
      `• Botões de ação (em cada card) — Permitem editar os dados de uma conta ou registrar o pagamento.\n\n` +
      `🎨 Status das Contas:\n\n` +
      `• 🟢 Verde = Paga\n` +
      `• 🟡 Amarelo = Pendente (dentro do prazo)\n` +
      `• 🔴 Vermelho = Vencida (prazo expirado sem pagamento)\n\n` +
      `💡 Dicas:\n` +
      `• Fique atento às contas próximas do vencimento para evitar atrasos e juros\n` +
      `• Use o filtro por Status para visualizar rapidamente apenas as contas pendentes ou vencidas\n` +
      `• Registre o pagamento de uma conta assim que ela for quitada para manter o fluxo de caixa atualizado`
  },
  '/sidebar/ContasReceberDropdown': {
    title: 'Contas a Receber',
    content:
      `Aqui você acompanha todos os valores que sua empresa tem a receber — gerados automaticamente a partir de vendas aprovadas de serviços e produtos. A página é dividida em duas abas para facilitar a visualização.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Aba "Contas de Serviços" — contas geradas por vendas de serviços (vinculadas a orçamentos)\n` +
      `• Aba "Contas de Produtos" — contas geradas por vendas de produtos\n` +
      `• Cada card mostra: ID, cliente, valor total em destaque, data de vencimento, data de emissão, ID da venda, parcelas pagas/total e juros\n` +
      `• Filtro de busca por Cliente, Valor, Data de Vencimento ou Status\n` +
      `• Botão 🔄 Atualizar para recarregar os dados da API\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Filtro ("Filtrar por" + campo de texto) — Selecione o critério e digite para localizar contas rapidamente.\n` +
      `• Abas "Contas de Serviços" / "Contas de Produtos" — Alterna entre os dois grupos de contas.\n` +
      `• Botão "Abrir Conta" (em cada card) — Abre um painel de detalhes completo mostrando todas as informações da conta, a configuração de pagamento vinculada e a simulação das parcelas com juros calculados automaticamente.\n\n` +
      `🎨 Status das Contas:\n\n` +
      `• 🟢 Verde = Pago\n` +
      `• 🟠 Laranja = Pendente\n` +
      `• 🔴 Vermelho = Vencido\n\n` +
      `💡 Dicas:\n` +
      `• Contas são criadas automaticamente quando uma venda é aprovada — não é preciso cadastrar manualmente\n` +
      `• Datas de vencimento ultrapassadas aparecem em vermelho no painel de detalhes para facilitar a identificação\n` +
      `• O painel de detalhes simula o cálculo de juros (simples ou compostos, fixo ou percentual) com base na configuração de pagamento da venda`
  },
  '/sidebar/ProducoesDropdown': {
    title: 'Projetos',
    content:
      `Aqui você gerencia os projetos da empresa. Um projeto é vinculado a um contrato e a um cliente, com controle de custo, tarefas, horas, matéria-prima, serviços terceirizados, entregas e montagens.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todos os projetos com: ID, título, cliente, contrato, orçamento, custo orçado, custo acumulado, horas, status de custo, status e as datas previstas e reais\n` +
      `• Filtro de busca por ID, Cliente, Status, Status de Custo, Título ou Contrato\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário do projeto: título, status, Conclusão do Desenvolvimento (prevista e real) e Finalização do Projeto (prevista; a real vem da aprovação do cliente)\n` +
      `• Quadro "Entregas, Montagens e Aceite Final" — Mostra o que já foi entregue e montado, o que falta e os botões do link de aprovação\n\n` +
      `⚠️ Como um projeto é finalizado:\n\n` +
      `• O status Finalizado não se escolhe: o projeto só é finalizado quando o cliente confirma o aceite final pelo link de aprovação\n` +
      `• Para enviar o link: todas as tarefas concluídas ou canceladas, ao menos uma entrega concluída, nenhuma entrega em aberto e, se alguma entrega indicou montagem, ao menos uma tarefa do tipo Montagem concluída\n` +
      `• Quando o cliente aprova, o sistema grava a data, gera o PDF com todas as informações e envia por e-mail ao cliente e ao dono da empresa\n` +
      `• A garantia do contrato começa a contar na data de aprovação\n` +
      `• Projeto finalizado não aceita mais entregas nem montagens; garantia e manutenção só dentro do prazo de garantia\n` +
      `• As datas de entrega e de montagem ficam em cada entrega e em cada tarefa de montagem, não no projeto\n\n` +
      `🎨 Status de Custo:\n\n` +
      `• 🟢 Verde = Dentro do orçamento\n` +
      `• 🟡 Amarelo = Em risco (aproximando do limite)\n` +
      `• 🔴 Vermelho = Estourado\n\n` +
      `💡 Dicas:\n` +
      `• Acompanhe os prazos no menu Prazos\n` +
      `• O PDF de aprovação fica disponível para baixar no formulário do projeto`
  },
  '/sidebar/ProjetoVisualDropdown': {
    title: 'Projeto Visual',
    content:
      `Aqui você visualiza e gerencia os projetos visuais vinculados aos projetos. Um Projeto Visual é o registro do design e documentação gráfica de um projeto — como plantas, renders, PDFs e imagens técnicas associadas a um projeto ou contrato.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de projetos visuais com: ID, ID do Cliente, ID da Oportunidade, ID do Contrato, ID do Projeto, Tipo de Documento e Número Externo do Projeto\n` +
      `• Filtro de busca por diferentes campos\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Ver Detalhes" (em cada card) — Abre um painel somente-leitura com todos os dados do projeto visual.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário de edição onde você pode:\n` +
      `  → Alterar a Descrição, Tipo de Documento (DWG, PDF, PNG, JPG, SKP, RVT, OUTROS) e Número Externo\n` +
      `  → Fazer upload de novas imagens (arrastar e soltar)\n` +
      `  → Fazer upload de novos PDFs\n` +
      `  → Baixar ou excluir arquivos já salvos\n\n` +
      `💡 Dicas:\n` +
      `• Os projetos visuais não são criados por esta página — eles são gerados automaticamente ao vincular um projeto a um projeto\n` +
      `• Use os tipos corretos de documento para facilitar a organização: DWG para CAD, RVT para Revit, SKP para SketchUp, etc.\n` +
      `• Você pode ter múltiplas imagens e PDFs por projeto visual`
  },
  '/sidebar/TarefasProjetoDropdown': {
    title: 'Tarefas do Projeto',
    content:
      `Aqui você gerencia todas as tarefas vinculadas aos projetos. Cada tarefa é uma etapa de trabalho atribuída a um ou mais responsáveis, com as horas reais trabalhadas (as horas previstas ficam no orçamento e a comparação está na Gestão Financeira), datas de início e fim, e acompanhamento de status.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todas as tarefas com: ID, descrição, projeto vinculado, cliente, datas de início e fim previstos, horas reais, tipo e status\n` +
      `• Badge de status colorido em cada card para identificação visual rápida\n` +
      `• Filtro de busca por Descrição, Status, Responsável ou ID do Projeto\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "+ Adicionar Tarefa" — Abre primeiro um seletor de projeto (com filtro e cards de projetos disponíveis). Após escolher o projeto, abre o formulário de criação com campos de descrição, datas, tipo de tarefa, responsáveis e suas horas.\n` +
      `• Botão "Ver Detalhes" (em cada card) — Abre o painel completo somente-leitura da tarefa com todos os dados, responsáveis e horas registradas.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário de edição para alterar qualquer dado da tarefa, incluindo adicionar/remover responsáveis e ajustar horas.\n` +
      `• Dentro dos formulários, você também pode criar um novo Responsável on-the-fly sem sair da página.\n\n` +
      `🎨 Status das Tarefas:\n\n` +
      `• 🟢 Verde = Concluída\n` +
      `• 🔵 Azul = Em Andamento\n` +
      `• 🟡 Amarelo = Pendente\n` +
      `• 🔴 Vermelho = Atrasada\n` +
      `• ⚪ Cinza = Cancelada\n\n` +
      `💡 Dicas:\n` +
      `• As horas das tarefas alimentam diretamente o custo acumulado do projeto — mantenha as horas reais atualizadas\n` +
      `• Cada responsável tem uma taxa de custo/hora que é usada no cálculo automático do custo\n` +
      `• Use o filtro por Status para ver apenas as tarefas atrasadas ou pendentes`
  },
  '/sidebar/ProdutoDropdown': {
    title: 'Produtos',
    content:
      `Aqui você cadastra e gerencia todos os produtos da sua empresa. Cada produto possui dados comerciais (nome, preço, categoria, estoque) e dados fiscais completos necessários para a emissão de Notas Fiscais Eletrônicas (NF-e). Sem os dados fiscais preenchidos corretamente, o sistema não consegue emitir notas fiscais no Brasil.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de produtos em formato de cards com: ID, nome, categoria, preço (R$), estoque disponível (físico − reservado) e status (🟢 Ativo / 🔴 Inativo)\n` +
      `• Filtro por ID, Nome ou Título do Produto\n` +
      `• Painel lateral de Categorias (botão vertical "CATEGORIAS" na borda direita)\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Adicionar Produto" — Abre o formulário completo de criação com todas as seções (dados gerais, estoque, fiscal, peso/medidas).\n` +
      `• Botão "📥 Cadastro em Massa" — Abre o popup de importação via planilha Excel para cadastrar múltiplos produtos de uma vez.\n` +
      `• Botão "CATEGORIAS" (lateral direita) — Abre o painel lateral onde você gerencia as categorias de produtos.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário com todos os dados carregados para alteração.\n\n` +
      `📦 Controle de Estoque:\n\n` +
      `• Estoque Físico — quantidade real em estoque\n` +
      `• Estoque Reservado — quantidade já comprometida em vendas/projetos\n` +
      `• Estoque Disponível = Físico − Reservado (calculado automaticamente)\n` +
      `• Estoque Mínimo — alerta quando o disponível ficar abaixo deste valor\n\n` +
      `📋 Dados Fiscais (NF-e) — OBRIGATÓRIOS para emitir notas:\n\n` +
      `• NCM (8 dígitos) — Código da mercadoria na tabela da SEFAZ\n` +
      `• CFOP — Código Fiscal de Operação (ex: 5102 = venda dentro do estado)\n` +
      `• CEST — Código Especificador de Substituição Tributária (quando aplicável)\n` +
      `• Origem da Mercadoria — Nacional, Estrangeira, etc. (9 opções)\n` +
      `• GTIN — Código de barras do produto (preenchido automaticamente se informar código de barras)\n` +
      `• Regime Tributário — Simples Nacional ou Lucro Real/Presumido\n` +
      `• CSOSN (Simples) ou CST ICMS (Regime Normal) — código de situação tributária\n` +
      `• CST IPI, CST PIS/COFINS — códigos tributários adicionais\n` +
      `• Unidade Tributária, Benefício Fiscal, Informações Adicionais\n\n` +
      `⚖️ Peso e Medidas:\n\n` +
      `• Peso Líquido e Peso Bruto (kg) — usados no cálculo de frete e na NF-e\n` +
      `• Altura, Largura e Comprimento (cm) — opcionais, para transporte\n\n` +
      `💡 Dicas:\n` +
      `• Preencha todos os campos da seção "Dados Fiscais (NF-e)" antes de tentar emitir notas — sem NCM e CFOP a nota será rejeitada pela SEFAZ\n` +
      `• Use o Cadastro em Massa para importar produtos de planilhas Excel quando tiver muitos itens\n` +
      `• Organize os produtos em categorias para facilitar a busca e os relatórios`
  },
  '/sidebar/FornecedorDropdown': {
    title: 'Fornecedores',
    content: `Aqui ficam cadastrados todos os seus fornecedores — sejam eles de materiais, produtos, serviços ou qualquer outro tipo de insumo que sua empresa utiliza. Esta é a central de gestão dos seus parceiros de abastecimento.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Fornecedores de matéria-prima e materiais\n` +
      `• Fornecedores de produtos acabados\n` +
      `• Prestadores de serviços terceirizados\n` +
      `• Distribuidores e representantes comerciais\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "+ Adicionar" — Abre o formulário para cadastrar um novo fornecedor com todos os dados (nome, CNPJ/CPF, endereço, telefone, e-mail, segmento e tipo).\n` +
      `• Card de Estatísticas — Mostra o total de fornecedores cadastrados atualmente.\n` +
      `• Filtro de Busca — Permite buscar fornecedores por Nome, Cidade, Segmento ou CNPJ/CPF. Use o dropdown ao lado para trocar o critério de busca.\n` +
      `• Botão "Ver Detalhes" (em cada card) — Abre uma janela com todas as informações completas do fornecedor.\n` +
      `• Botão "Editar" (em cada card) — Permite alterar os dados do fornecedor já cadastrado.\n\n` +
      `💡 Dicas:\n` +
      `• Mantenha os dados de contato dos fornecedores sempre atualizados\n` +
      `• Use os segmentos para organizar seus fornecedores por categoria\n` +
      `• Cadastre todos os fornecedores para ter controle total das suas compras e insumos`
  },
  '/sidebar/ResponsaveisDropdown': {
    title: 'Pessoas',
    content: `Aqui ficam cadastradas todas as pessoas da sua empresa — colaboradores, responsáveis e profissionais que participam dos projetos, tarefas e projetos do seu negócio. Pessoas são fundamentais para atribuir responsáveis em etapas de projeto e calcular custos de mão de obra.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Colaboradores internos da empresa\n` +
      `• Responsáveis por tarefas e etapas de projeto\n` +
      `• Profissionais com custo/hora definido para cálculos de projeto\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Nova Pessoa" — Abre o formulário para cadastrar uma nova pessoa, informando o nome e o custo/hora.\n` +
      `• Filtro de Busca — Permite buscar pessoas pelo nome. A lista é filtrada automaticamente conforme você digita.\n` +
      `• Card de Estatísticas — Mostra o total de pessoas cadastradas e o custo/hora médio de todos os colaboradores.\n` +
      `• Botão "Ver Detalhes" (em cada card) — Abre uma janela com todas as informações da pessoa.\n` +
      `• Botão "Editar" (em cada card) — Permite alterar o nome e o custo/hora da pessoa.\n\n` +
      `💡 Dicas:\n` +
      `• Defina o custo/hora de cada pessoa para ter cálculos precisos nos projetos\n` +
      `• Cadastre todos os colaboradores que participam de tarefas e projetos\n` +
      `• Use o filtro para encontrar rapidamente quem você procura`
  },
  '/sidebar/NotaFiscalDropdown': {
    title: 'Notas Fiscais',
    content:
      `Aqui você emite, acompanha e gerencia todas as notas fiscais da empresa — tanto NF-e (Nota Fiscal Eletrônica de Produtos) quanto NFS-e (Nota Fiscal de Serviços). A integração é feita diretamente com a SEFAZ via plataforma PlugNotas.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Alternador de tipo no topo: "Notas de Produtos" (NF-e) ou "Notas de Serviços" (NFS-e)\n` +
      `• Abas "Pendentes" e "Emitidas" para organizar as notas por status\n` +
      `• Lista de notas com número, ID, nome do cliente, valor e status com cor\n` +
      `• Seleção múltipla de notas via checkbox para operar em lote\n` +
      `• Filtro de busca por diversos critérios\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Filtro — Filtre notas por cliente, número, valor, status ou outros critérios.\n` +
      `• Clicar em uma nota — Abre o painel completo de visualização com todos os dados fiscais (chave de acesso, protocolo, ambiente), itens da venda, dados do comprador e link para baixar o PDF.\n` +
      `• "Autorizar Nota(s)" (aba Pendentes, NF-e) — Envia as notas selecionadas para autorização na SEFAZ. O progresso aparece em uma barra lateral direita. Erros ficam disponíveis para consulta e correção.\n` +
      `• "Baixar Nota(s)" — Baixa as notas selecionadas em PDF ou XML.\n` +
      `• "Editar Nota" — Abre o formulário de edição da nota (disponível quando exatamente 1 nota está selecionada).\n` +
      `• "Imprimir Nota(s)" — Imprime as notas selecionadas.\n` +
      `• "Cancelar Nota(s)" (aba Emitidas) — Cancela as notas emitidas selecionadas junto à SEFAZ.\n` +
      `• "Excluir" (aba Pendentes) — Remove as notas pendentes selecionadas.\n\n` +
      `🎨 Status das Notas:\n\n` +
      `• 🟢 Verde = Emitida / Autorizada\n` +
      `• 🟠 Laranja = Pendente / Aguardando\n` +
      `• 🔴 Vermelho = Cancelada\n\n` +
      `💡 Dicas:\n` +
      `• Use a aba "Pendentes" para emitir notas em lote: selecione várias e clique em "Autorizar Notas"\n` +
      `• Quando uma autorização falha, clique em "Ver" na barra lateral para abrir o painel de erros — lá você pode corrigir dados do produto, da venda ou do comprador e tentar novamente\n` +
      `• O painel de detalhes de cada nota mostra o ambiente de emissão: Produção (real) ou Homologação (testes)`
  },
  '/sidebar/CertificadoPlugNotasDropdown': {
    title: 'Certificado Digital',
    content:
      `O Certificado Digital A1 é obrigatório para emitir notas fiscais eletrônicas no Brasil. Sem ele, o sistema de Notas Fiscais não consegue se comunicar com a SEFAZ (Secretaria da Fazenda) para autorizar as emissões.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de certificados A1 cadastrados (arquivo .pfx ou .p12)\n` +
      `• Status de cada certificado: ativo ou inativo\n` +
      `• Data de vencimento com alerta visual quando expirado\n` +
      `• ID de integração com a plataforma PlugNotas\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• "+ Novo Certificado" — Abre o formulário para cadastrar um novo certificado. Você faz upload do arquivo .pfx ou .p12 (arrastar e soltar ou clique), informa a senha do certificado e opcionalmente um e-mail para notificações de vencimento. O CNPJ é preenchido automaticamente com o da empresa logada.\n` +
      `• "🔄 Renovar" (em cada card) — Expande um formulário inline para substituir o certificado vencido ou expirado por um novo arquivo, sem perder o vínculo com a empresa.\n` +
      `• "🗑️ Excluir" (em cada card) — Remove o certificado do sistema e também da plataforma PlugNotas. Esta ação não pode ser desfeita.\n\n` +
      `🎨 Status do Certificado:\n\n` +
      `• 🟢 Verde = Ativo\n` +
      `• 🔴 Vermelho = Inativo / Vencido (data marcada com ⚠️ VENCIDO)\n\n` +
      `💡 Dicas:\n` +
      `• O certificado A1 tem validade de 1 ou 3 anos — fique atento à data de vencimento para não perder a capacidade de emitir notas\n` +
      `• Após cadastrar ou renovar o certificado, ele é sincronizado automaticamente com a plataforma PlugNotas\n` +
      `• Você só precisa de um certificado por empresa (CNPJ)`
  },
  '/sidebar/OportunidadeDropdown': {
    title: 'Oportunidades',
    content: `Aqui você gerencia todas as oportunidades comerciais da sua empresa. Uma oportunidade representa um potencial negócio com um cliente — pode ser uma venda, um projeto, uma proposta ou qualquer chance de fechar um contrato.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todas as oportunidades vinculadas a clientes\n` +
      `• Status de cada oportunidade (em andamento, fechada, perdida, etc.)\n` +
      `• Data de criação e título de cada oportunidade\n` +
      `• Acesso rápido para editar qualquer oportunidade\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão \"Nova Oportunidade\" — Abre um popup para escolher o cliente ao qual a oportunidade será vinculada. Após escolher o cliente, abre o formulário de criação da oportunidade.\n` +
      `• Filtro de Busca — Permite filtrar as oportunidades por: ID da Oportunidade, ID do Cliente, Nome do Cliente, Título ou Status. Selecione o critério no dropdown e digite no campo.\n` +
      `• Botão \"Editar\" (em cada card) — Abre o formulário de edição da oportunidade selecionada.\n\n` +
      `📊 Campos do Card:\n\n` +
      `• ID Oportunidade — Identificador único\n` +
      `• ID do Cliente — A qual cliente está vinculada\n` +
      `• Nome do Cliente — Nome do cliente associado\n` +
      `• Título — Descrição resumida da oportunidade\n` +
      `• Status — Situação atual (ex: Em Andamento, Fechada, Perdida)\n` +
      `• Data Criação — Quando foi registrada\n\n` +
      `💡 Dicas:\n` +
      `• Mantenha o status de cada oportunidade sempre atualizado para ter um pipeline comercial preciso\n` +
      `• Use o filtro por Nome do Cliente para ver todas as oportunidades de um cliente específico\n` +
      `• Agendamentos e Orçamentos podem ser vinculados a uma oportunidade`
  },
  '/sidebar/AgendamentosDropdown': {
    title: 'Agendamentos',
    content: `Aqui você gerencia todos os seus agendamentos e compromissos com clientes. Agendamentos são vinculados a clientes e oportunidades, permitindo organizar visitas, reuniões, entregas e qualquer compromisso do seu negócio.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Agendamentos de visitas a clientes\n` +
      `• Compromissos vinculados a oportunidades de venda\n` +
      `• Controle de horários e datas\n` +
      `• Indicadores de atraso e prazo vencendo\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "+" (Adicionar) — Inicia o fluxo de criação: primeiro escolha um cliente, depois uma oportunidade vinculada, e então preencha o formulário de agendamento.\n` +
      `• Filtro de Busca — Filtre agendamentos por nome do cliente, ID, ID do cliente, ou CPF/CNPJ. Use o dropdown para trocar o critério. Também filtre por status (A Visitar, Agendado, etc).\n` +
      `• Botão \"Atrasados\" — Mostra apenas os agendamentos que já passaram da data e ainda não foram concluídos.\n` +
      `• Botão \"Editar\" (em cada card) — Abre o formulário com os dados do agendamento para edição.\n\n` +
      `⏰ Lógica dos Horários no Formulário:\n\n` +
      `No formulário de agendamento, você escolhe o horário através de uma grade com blocos de 30 minutos (de 00:00 às 23:30). O primeiro clique marca o INÍCIO, o segundo clique marca o FIM — todos os blocos entre eles ficam selecionados.\n\n` +
      `🎨 Cores dos Horários:\n\n` +
      `• 🟠 LARANJA — Sua seleção atual (início, fim e todos os blocos entre eles)\n` +
      `• 🟢 VERDE — Horário do agendamento que você está editando (só aparece em edição)\n` +
      `• 🔴 VERMELHO — Horários já ocupados por outros agendamentos na mesma data\n` +
      `• ⚪ CINZA — Horários disponíveis/livres\n\n` +
      `💡 Dicas:\n` +
      `• Se clicar em um horário vermelho (ocupado), o sistema pergunta se deseja prosseguir mesmo assim\n` +
      `• O indicador vermelho \"Atrasado\" aparece nos cards de agendamentos que já passaram da data\n` +
      `• O indicador amarelo \"Prazo vencendo\" aparece quando faltam 5 dias ou menos`
  },
  '/sidebar/CalendarioDropdown': {
    title: 'Calendário',
    content: `Esta é a visualização em calendário dos seus agendamentos. Aqui você vê todos os compromissos organizados por dia no mês, facilitando a visão geral da sua agenda.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Grade mensal com todos os dias do mês\n` +
      `• Contagem de agendamentos em cada dia\n` +
      `• Nomes dos primeiros clientes agendados no dia\n` +
      `• Indicadores de atraso (vermelho) e prazo vencendo (laranja)\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Setas \"\u2190\" e \"\u2192\" — Navegam entre os meses (anterior e próximo).\n` +
      `• Seletores de Mês e Ano — Permitem pular diretamente para qualquer mês/ano.\n` +
      `• Passar o mouse sobre um dia — Aparece um overlay com botão \"Ver todos\" (se tem agendamentos) ou \"+\" (se está vazio).\n` +
      `• Botão \"Ver todos\" — Abre um popup com todos os agendamentos daquele dia, onde você pode editar ou criar novos.\n` +
      `• Botão \"+\" — Abre o fluxo de criação de novo agendamento para aquele dia.\n\n` +
      `💡 Dicas:\n` +
      `• Dias com texto vermelho indicam agendamentos atrasados\n` +
      `• Use os seletores para navegar rapidamente até meses futuros ou passados\n` +
      `• O formulário de agendamento aberto pelo calendário funciona igual ao da página de Agendamentos`
  },
  '/sidebar/EstoqueMaterialDropdown': {
    title: 'Estoque de Materiais',
    content: `Controle o estoque de matéria-prima e materiais.\n\n` +
      `📌 Funcionalidades:\n\n` +
      `• Cadastrar materiais e matéria-prima\n` +
      `• Controlar entradas e saídas\n` +
      `• Definir estoque mínimo\n` +
      `• Acompanhar nível de estoque atual\n\n` +
      `💡 Dicas:\n` +
      `• Configure alertas de estoque mínimo\n` +
      `• Registre todas as movimentações para controle preciso`
  },
  '/sidebar/MateriaPrimaDropdown': {
    title: 'Matéria-Prima',
    content:
      `Aqui você visualiza e gerencia toda a matéria-prima consumida ou alocada nos projetos. Cada item de matéria-prima está vinculado a um projeto e a um cliente, com controle de quantidade utilizada e quantidade faltante.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todos os itens de matéria-prima com: ID, descrição, tipo, valor, fornecedor, ID do projeto, ID do cliente, quantidade e data de execução\n` +
      `• Indicador vermelho de Quantidade Faltante quando há déficit (borda do card fica vermelha)\n` +
      `• Contador no topo: Total de Itens e Valor Total filtrados\n` +
      `• Filtro por Descrição, Tipo, Fornecedor ou ID de Projeto\n` +
      `• Botão "Materiais em falta" para ver apenas itens com déficit\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• "Materiais em falta" — Filtra a lista mostrando apenas os itens com Quantidade Faltante > 0. Use para identificar rapidamente o que precisa ser providenciado.\n` +
      `• Botão "Ver Detalhes" (em cada card) — Abre o painel somente-leitura com todos os dados do item incluindo quantidade disponível no estoque.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário de edição onde você pode alterar: Descrição, Quantidade, Tipo, Valor e Data de Execução. Também exibe o estoque disponível (somente-leitura) do material vinculado.\n\n` +
      `🎨 Indicadores Visuais:\n\n` +
      `• 🟠 Borda laranja = item normal\n` +
      `• 🔴 Borda vermelha = item com quantidade faltante (campo "Faltante" destacado em vermelho no card)\n\n` +
      `💡 Dicas:\n` +
      `• A página aceita links diretos com filtros — ao clicar em "materiais faltantes" na página de Projetos, você é redirecionado aqui já filtrado pelo projeto e com o filtro de faltantes ativo\n` +
      `• O campo "Disponível no Estoque" no formulário mostra o saldo atual do material no Estoque de Materiais — use isso para saber se precisa repor\n` +
      `• Itens faltantes bloqueiam o fluxo de edição do projeto até serem resolvidos`
  },
  '/sidebar/EntregaServicosDropdown': {
    title: 'Entregas de Serviços',
    content:
      `Aqui você agenda e conclui as entregas dos projetos. Toda entrega pertence a um projeto. Nada é criado sozinho: agende quantas entregas forem necessárias, em qualquer status do projeto, por exemplo entregando partes do móvel e montando tudo depois.\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• "+ Agendar Entrega" — Escolha o projeto e informe a data prevista e o local\n` +
      `• Tarefa do projeto (opcional) — Ligue a entrega a uma tarefa. Tarefa do tipo Montagem marca a entrega como 🔧 Montagem\n` +
      `• Ao concluir uma tarefa do tipo Montagem sem entrega ligada, o sistema cria sozinho a entrega de montagem, agendada, para você concluir aqui com a foto\n` +
      `• "✅ Concluir" — Registra a data real, o que foi entregue e as fotos\n` +
      `• "✏️ Editar" — Altera os dados, os responsáveis e as fotos, ou cancela a entrega\n` +
      `• "📅" — Calendário com as entregas previstas em cada dia\n\n` +
      `✅ Concluir a entrega:\n\n` +
      `• A foto é obrigatória. Ela comprova a entrega e não é a aprovação do projeto\n` +
      `• Informe se ainda há entregas pendentes\n` +
      `• Informe se vai ter montagem. Se sim, o projeto só vai para a aprovação do cliente com uma tarefa do tipo Montagem concluída\n\n` +
      `💡 Dicas:\n` +
      `• Montagem é uma tarefa do tipo Montagem (Projeto → Tarefas)\n` +
      `• O que você escreve em "O que foi entregue" aparece para o cliente no link de aprovação\n` +
      `• Projeto finalizado não aceita mais entregas`
  },
  '/sidebar/EntregasProdutosDropdown': {
    title: 'Entregas de Produtos',
    content:
      `Aqui você agenda e conclui as entregas das vendas de produto. Toda entrega pertence a uma venda. A primeira nasce quando a venda é aprovada; se precisar entregar em partes, diminua a quantidade dela e agende outra entrega para a mesma venda.\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• "+ Agendar Entrega" — Escolha a venda aprovada e informe a data prevista e a quantidade\n` +
      `• "✅ Concluir" — Registra a data real e as fotos e dá baixa no estoque\n` +
      `• "✏️ Editar" — Altera os dados e as fotos, muda o status ou cancela a entrega\n` +
      `• "📅" — Calendário com as entregas previstas em cada dia\n\n` +
      `✅ Concluir a entrega:\n\n` +
      `• A foto é obrigatória. Ela comprova a entrega e não é a aprovação do projeto\n` +
      `• Informe se ainda há entregas pendentes\n` +
      `• A soma das entregas não passa da quantidade vendida\n\n` +
      `💡 Dicas:\n` +
      `• Cancelar uma entrega não cancela a venda: a reserva do estoque continua para a próxima entrega`
  },
  '/sidebar/PrazosDropdown': {
    title: 'Prazos',
    content:
      `Aqui você acompanha o previsto e o concluído de cada etapa, com a situação calculada automaticamente.\n\n` +
      `📌 Abas:\n\n` +
      `• Desenvolvimento: datas do formulário do projeto\n` +
      `• Entregas de Projetos e Entregas de Produtos: separadas, sem misturar\n` +
      `• Montagem: tarefas do tipo Montagem (data fim prevista e real)\n` +
      `• Aprovação do Projeto: finalização prevista e data em que o cliente aprovou\n\n` +
      `🎨 Situação:\n\n` +
      `• Concluído com Atraso: concluído depois do previsto\n` +
      `• Concluído Dentro do Prazo: concluído até o previsto\n` +
      `• Atrasado: sem conclusão e a data de hoje chegou no previsto ou passou dele\n` +
      `• Risco de Atraso: sem conclusão e faltam menos de 7 dias\n` +
      `• Dentro do Prazo: sem conclusão e faltam 7 dias ou mais\n` +
      `• Sem Previsão: a data prevista ainda não foi preenchida\n\n` +
      `💡 Dicas:\n` +
      `• Filtre por ID ou cliente e pela situação\n` +
      `• Itens cancelados não aparecem`
  },
  '/sidebar/EstoqueMaterialDropdown': {
    title: 'Estoque de Materiais',
    content: `Controle o estoque de matéria-prima e materiais.\n\n` +
      `📌 Funcionalidades:\n\n` +
      `• Cadastrar materiais e matéria-prima\n` +
      `• Controlar entradas e saídas\n` +
      `• Definir estoque mínimo\n` +
      `• Acompanhar nível de estoque atual\n\n` +
      `💡 Dicas:\n` +
      `• Configure alertas de estoque mínimo\n` +
      `• Registre todas as movimentações para controle preciso`
  },
  '/sidebar/GarantiaDropdown': {
    title: 'Garantias',
    content:
      `Aqui você gerencia todas as garantias vinculadas às seus projetos e contratos. Cada garantia registra o problema reportado, o custo, o responsável e o status atual, permitindo acompanhar desde a solicitação até a conclusão ou recusa.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de garantias em formato de cards com: ID, contrato vinculado, projeto, cliente, data de solicitação, custo (R$), descrição do problema e responsável\n` +
      `• Badge de status colorido em cada card: 🟠 Solicitada (laranja), 🔵 Em Análise (azul), 🟢 Aprovada (verde), 🟣 Em Andamento (roxo), 🟢 Concluída (verde), 🔴 Recusada (vermelho), ⚫ Cancelada (cinza)\n` +
      `• Barra de estatísticas: Total de garantias cadastradas (filtradas)\n` +
      `• Filtro por ID da Garantia, ID do Projeto, ID do Cliente, ID do Contrato, Status ou Data\n` +
      `• Calendário mensal com indicadores visuais: 🔴 Atrasado (vencido) e 🟠 Vencendo (≤ 3 dias)\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Adicionar Garantia" — Abre primeiro o seletor de projeto (com filtro por ID, Contrato, Oportunidade, Título, Cliente ou Venda). Após escolher o projeto, abre o formulário de criação com todos os campos, incluindo alocação de responsáveis e matéria-prima.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário de edição completo com os dados carregados da garantia.\n` +
      `• Botão "📅" — Abre o calendário mensal onde cada dia mostra quantas garantias existem. Clique num dia para ver a lista detalhada (ID, Cliente, Hora, Status, Descrição) com opção de editar cada uma.\n\n` +
      `👤 Responsáveis:\n\n` +
      `• No formulário você pode adicionar responsáveis à garantia, informando horas previstas e horas reais\n` +
      `• É possível criar uma nova Pessoa diretamente pelo botão dentro do formulário (Nome e Custo/Hora)\n\n` +
      `💡 Dicas:\n` +
      `• Use o calendário para identificar rapidamente garantias atrasadas (vermelho) ou prestes a vencer (laranja)\n` +
      `• Mantenha o status da garantia atualizado (Solicitada → Em Análise → Aprovada → Em Andamento → Concluída) para acompanhar o fluxo completo\n` +
      `• O vínculo com Contrato e Projeto permite rastrear a origem de cada garantia`
  },
  '/sidebar/ManutencaoDropdown': {
    title: 'Manutenção',
    content:
      `Aqui você gerencia todas as manutenções vinculadas às seus projetos. Cada manutenção registra a descrição do serviço, o responsável, o custo e a data de execução, garantindo o controle completo dos reparos e ajustes realizados.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de manutenções em formato de cards com: ID, projeto vinculado, cliente, responsável, data de execução, custo (R$), descrição e pessoa alocada\n` +
      `• Barra de estatísticas: Total de manutenções cadastradas (filtradas)\n` +
      `• Filtro por ID da Manutenção, ID do Projeto, ID do Cliente, Responsável ou Data\n` +
      `• Calendário mensal com indicadores visuais: 🔴 Atrasado (vencido) e 🟠 Vencendo (≤ 3 dias)\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Adicionar Manutenção" — Abre primeiro o seletor de projeto (com filtro por ID, Contrato, Oportunidade, Título, Cliente ou Venda). Após escolher o projeto, abre o formulário de criação com todos os campos, incluindo alocação de responsáveis e matéria-prima.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário de edição com os dados carregados da manutenção.\n` +
      `• Botão "📅" — Abre o calendário mensal onde cada dia mostra quantas manutenções existem. Clique num dia para ver a lista detalhada (ID, Cliente, Hora, Responsável, Descrição) com opção de editar cada uma.\n\n` +
      `👤 Responsáveis:\n\n` +
      `• No formulário é possível adicionar responsáveis à manutenção, informando horas previstas e horas reais\n` +
      `• É possível criar uma nova Pessoa diretamente pelo formulário (Nome e Custo/Hora)\n\n` +
      `💡 Dicas:\n` +
      `• Use o calendário para identificar manutenções atrasadas (vermelho) ou próximas do prazo (laranja)\n` +
      `• Registre o custo de cada manutenção para acompanhar o impacto financeiro nos projetos\n` +
      `• Mantenha o campo de responsável preenchido para saber quem executou cada serviço`
  },
  '/sidebar/ServicosTerceirosDropdown': {
    title: 'Serviços Terceiros',
    content:
      `Aqui você gerencia todos os serviços terceirizados contratados para os projetos. Cada serviço de terceiro está vinculado a um projeto e a um fornecedor, e seu custo alimenta automaticamente o custo acumulado do projeto.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todos os serviços terceirizados com: ID, descrição, projeto vinculado, cliente, fornecedor, tipo de serviço, valor e data de execução\n` +
      `• Badge de status colorido em cada card: 🟢 Concluído (verde), 🔵 Em Andamento (azul), 🟠 Pendente (laranja) e 🔴 Cancelado (vermelho)\n` +
      `• Barra de estatísticas no topo: Total de Serviços e Valor Total (filtrados)\n` +
      `• Filtro por Descrição, Status, Fornecedor ou ID do Projeto\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Adicionar" — Abre primeiro um seletor de projeto (com filtro e cards dos projetos disponíveis). Após escolher o projeto, abre o formulário de criação onde você preenche: cliente, fornecedor, tipo de serviço, valor, data de execução, status e descrição.\n` +
      `• Botão "Ver Detalhes" (em cada card) — Abre o painel somente-leitura com todos os dados do serviço.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário de edição completo para alterar qualquer campo. O ID do projeto pode ser alterado aqui (diferente da criação onde é fixo).\n\n` +
      `🎨 Status dos Serviços:\n\n` +
      `• 🟢 Verde = Concluído\n` +
      `• 🔵 Azul = Em Andamento\n` +
      `• 🟠 Laranja = Pendente\n` +
      `• 🔴 Vermelho = Cancelado\n\n` +
      `💡 Dicas:\n` +
      `• O valor de cada serviço contribui para o custo acumulado do projeto — confira o status de custo na página de Projetos\n` +
      `• Use o filtro por Projeto ID para ver todos os serviços contratados para um projeto específica\n` +
      `• Mantenha o status atualizado para cada serviço (Pendente → Em Andamento → Concluído) para acompanhar o progresso`
  },
  '/sidebar/RelacionamentoClientesDropdown': {
    title: 'Pós-Venda — Relacionamento com Clientes',
    content:
      `Aqui você gerencia todo o relacionamento pós-venda com seus clientes. Esta página é o centro de ações de marketing, fidelização e pesquisa de satisfação — serve para enviar e-mails personalizados, agendar ligações de follow-up, registrar pesquisas de satisfação, campanhas de upsell/cross-sell e qualquer outro contato planejado com o cliente.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de relacionamentos em formato de cards com: ID, tipo de evento (com ícone), cliente, data agendada, e-mail e celular do cliente\n` +
      `• Badge de status colorido em cada card: 🔵 Agendado (azul), 🟠 Em Andamento (laranja), 🟢 Concluído (verde), ⚫ Cancelado (cinza), 🟣 Reagendado (roxo), 🔴 Aguardando Retorno (vermelho escuro)\n` +
      `• Painel de estatísticas no topo com 3 contadores: Agendado, Em Andamento e Concluído\n` +
      `• Filtro por ID, Cliente, Tipo de Evento, Status ou Data\n\n` +
      `📋 Tipos de Evento disponíveis:\n\n` +
      `• 🏢 Visita pós-venda  •  📞 Ligação de follow-up  •  📧 E-mail de satisfação / Newsletter\n` +
      `• 📊 Pesquisa de feedback  •  👥 Reunião de acompanhamento  •  🛠️ Suporte / Atendimento\n` +
      `• 📚 Treinamento  •  🔧 Manutenção programada  •  📄 Renovação de contrato\n` +
      `• 💰 Upsell / Cross-sell  •  🎉 Evento corporativo  •  🎂 Aniversário do cliente\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "+ Adicionar Relacionamento" — Abre o formulário onde você seleciona o Cliente, escolhe o Tipo de Evento, define a Data e o Status inicial (padrão: Agendado).\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário com os dados carregados para alterar tipo de evento, data ou status.\n\n` +
      `💡 Dicas:\n` +
      `• Use esta página para planejar ações de marketing: agende e-mails de satisfação, campanhas de upsell e follow-ups com clientes recentes\n` +
      `• Os contadores no topo ajudam a acompanhar quantas ações estão pendentes (Agendado) e quantas já foram realizadas (Concluído)\n` +
      `• O e-mail e celular do cliente aparecem diretamente no card para facilitar o contato rápido`
  },
  '/sidebar/ForumEmpresas': {
    title: 'Fórum Empresas',
    content:
      `O Fórum Empresas é o espaço de conversa entre todas as empresas que usam o S6 Gestão — não só a sua, mas também as outras.\n\n` +
      `📌 Para que serve:\n\n` +
      `• Tirar dúvidas sobre o sistema e ajudar outras empresas com as suas experiências\n` +
      `• Acompanhar as novidades e atualizações do S6 Gestão assim que forem lançadas\n` +
      `• Sugerir o que você gostaria de ter no sistema: funções novas, melhorias e ajustes\n` +
      `• Dar sua opinião sobre as sugestões e novidades, curtindo e comentando as publicações\n` +
      `• Ver o que outras empresas estão comentando e compartilhando\n\n` +
      `🔘 Como usar:\n\n` +
      `• Publicar — o CEO da empresa escreve no campo do topo ("O que você está pensando?"), pode anexar imagens e clica em publicar\n` +
      `• Curtir — mostra que você concorda ou gostou da publicação\n` +
      `• Comentar — abra os comentários da publicação para responder, dar sua opinião ou tirar uma dúvida\n` +
      `• Excluir — quem publicou pode apagar a própria publicação pelo ícone de lixeira\n\n` +
      `💡 Dica:\n` +
      `• Entre de vez em quando para ficar por dentro de todas as atualizações do seu sistema`
  },
  '/sidebar/Configuracoes': {
    title: 'Configurações',
    content: `Configure o sistema de acordo com suas necessidades.\n\n` +
      `📌 Funcionalidades:\n\n` +
      `• Configurar dados da empresa\n` +
      `• Gerenciar usuários e permissões\n` +
      `• Ajustar preferências do sistema\n` +
      `• Configurar integrações\n\n` +
      `💡 Dicas:\n` +
      `• Revise as permissões dos usuários periodicamente\n` +
      `• Mantenha os dados da empresa atualizados`
  },
  '/sidebar/MinhaConta': {
    title: 'Minha Conta',
    content: `Gerencie suas informações pessoais.\n\n` +
      `📌 Funcionalidades:\n\n` +
      `• Atualizar dados do perfil\n` +
      `• Alterar foto de perfil\n` +
      `• Gerenciar configurações de notificação\n\n` +
      `💡 Dicas:\n` +
      `• Mantenha suas informações de contato atualizadas\n` +
      `• Use uma foto de perfil para facilitar a identificação`
  },
  '/sidebar/CadastrarUsuario': {
    title: 'Cadastrar Funcionários',
    content:
      `Aqui você pode cadastrar novos funcionários para a sua conta.\n\n` +
      `📌 Sobre o Cadastro:\n\n` +
      `• A quantidade de funcionários que você pode cadastrar depende do plano ativo na sua conta\n` +
      `• Você pode visualizar seu plano ativo em Configurações → Meus Planos\n` +
      `• Caso tenha excedido o limite de funcionários, você pode:\n` +
      `  → Comprar acesso para um novo usuário à parte\n` +
      `  → Fazer um upgrade do plano\n` +
      `  Ambas as opções estão disponíveis em Configurações → Meus Planos\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Adicionar Nova Conta" — Abre o formulário de cadastro do funcionário. Preencha login, senha, CPF, cargo, nível e dados pessoais. Ao final, defina as permissões de acesso às páginas do sistema.\n` +
      `• Botão "Gerenciar Funcionários" — Navega para a tela de gerenciamento onde você pode editar os funcionários já cadastrados.\n\n` +
      `🔐 Permissões de Acesso:\n\n` +
      `• As permissões dependem do nível do funcionário selecionado\n` +
      `• Gerente de TI: acesso total a todas as páginas (bloqueado para edição)\n` +
      `• Gerente: acesso padrão, com opção de permitir gerenciamento de outros funcionários\n` +
      `• Outros níveis: você pode selecionar manualmente cada módulo que o funcionário poderá acessar\n\n` +
      `💡 Dicas:\n` +
      `• Defina as permissões corretamente ao cadastrar para evitar ter que editar depois\n` +
      `• O login e a senha não podem conter espaços\n` +
      `• O CPF do Empregado deve ter 11 dígitos (somente números)`
  },
  '/sidebar/GerenciarFuncionarios': {
    title: 'Gerenciar Funcionários',
    content:
      `Aqui você edita e gerencia todas as contas de funcionários vinculadas à sua empresa.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Lista de todos os funcionários cadastrados com login, CPF, cargo e nível de acesso\n` +
      `• Opções de edição, alteração de senha e exclusão para cada funcionário\n` +
      `• Controle completo de permissões de acesso por funcionário\n\n` +
      `🔘 Botões e Funções:\n\n` +
      `• Botão "Cadastrar Novo Funcionário" — Navega para a tela de cadastro para adicionar um novo funcionário à sua conta.\n` +
      `• Botão "Alterar Senha" (em cada card) — Abre um popup para redefinir a senha do funcionário selecionado.\n` +
      `• Botão "Editar" (em cada card) — Abre o formulário completo de edição com dados pessoais, cargo, nível e permissões de acesso às páginas do sistema.\n` +
      `• Botão "Excluir" (em cada card) — Remove o funcionário do sistema após confirmação.\n\n` +
      `🔐 Permissões de Acesso (no formulário de edição):\n\n` +
      `• As permissões são controladas por nível do funcionário\n` +
      `• Você pode ajustar manualmente quais páginas um funcionário pode acessar, marcando ou desmarcando as caixinhas correspondentes e salvando\n\n` +
      `💡 Dicas:\n` +
      `• Revise as permissões periodicamente para garantir que cada funcionário acesse apenas o necessário\n` +
      `• Ao alterar o nível de um funcionário, as permissões são ajustadas automaticamente`
  },
  '/sidebar/CadastrarConta': {
    title: 'Conta de Recebimento',
    content:
      `Aqui é onde você cadastra o banco para receber por todas as suas vendas — seja de produtos, seja de serviços, qualquer venda gerada e vendida dentro da plataforma. Cadastre uma conta aqui para receber os seus pagamentos.\n\n` +
      `📌 O que você encontra aqui:\n\n` +
      `• Formulário em 3 etapas (indicado pelas 3 bolinhas no topo) para cadastrar sua conta de recebimento\n` +
      `• Após o cadastro, a plataforma verifica sua identidade e dados bancários via Stripe\n\n` +
      `🔘 Etapas do Cadastro:\n\n` +
      `• Etapa 1 — Dados Pessoais: nome, CPF ou CNPJ, e-mail, telefone e data de nascimento (ou dados da empresa para PJ)\n` +
      `• Etapa 2 — Endereço: endereço completo com rua, cidade, estado e CEP\n` +
      `• Etapa 3 — Conta Bancária: banco, agência, número da conta e tipo (corrente ou poupança)\n\n` +
      `✅ Verificação da Conta:\n\n` +
      `Após o cadastro, a conta passa por um processo de verificação em 3 etapas:\n` +
      `• Etapa 1: Conta criada — dados recebidos e registrados na plataforma\n` +
      `• Etapa 2: Verificação pendente — documentos em análise pela Stripe. Pode ser necessário enviar documentos de identidade\n` +
      `• Etapa 3: Conta ativa — verificação concluída, pagamentos e saques habilitados\n\n` +
      `💡 Dicas:\n` +
      `• Use o botão "Atualizar Status" para verificar se sua conta já foi aprovada\n` +
      `• Se houver documentos pendentes, eles aparecerão listados abaixo do status da conta\n` +
      `• Após a ativação, você poderá receber pagamentos via PIX, cartão ou boleto diretamente na sua conta`
  },
};

// Passos do tour guiado para a página Home
const homeTourSteps = [
  {
    selector: '.feed-card',
    title: 'Área de Postagem',
    text: 'Aqui você pode fazer postagens para sua equipe. Escreva um título, adicione uma descrição e anexe fotos ou arquivos. Se você só postar sem selecionar grupo, a postagem vai para o feed geral. Para postar em grupos específicos, clique no botão "Mais grupos" e escolha os grupos desejados.',
    position: 'bottom', // balão aparece embaixo do elemento
    expandComposer: true, // expande o FormularioPostagem antes de mostrar
  },
  {
    selector: '.sidebar-groups-list',
    fallbackSelector: '.sidebar-left',
    sectionTitle: 'Grupos',
    title: 'Seus Grupos',
    text: 'Aqui você pode gerenciar seus grupos, criar novos grupos, ver postagens de um grupo específico clicando nele, e muito mais. Use os grupos para organizar as comunicações da sua equipe.',
    position: 'right', // balão à direita do sidebar esquerdo
  },
  {
    selector: '.sidebar-conexoes-list',
    fallbackSelector: '.sidebar-left',
    sectionTitle: 'Minhas Conexões',
    title: 'Suas Conexões',
    text: 'Aqui você tem suas conexões. Você pode entrar no perfil de uma conexão, iniciar uma conversa, conectar-se com novas pessoas, desconectar-se ou ver todas as suas conexões clicando em "Ver todos".',
    position: 'right',
  },
  {
    selector: '.right-side',
    title: 'Atrasos e Notificações',
    text: 'Aqui você tem todos os agendamentos em atraso. Clique em um card para abrir esse agendamento específico. Use o botão "Ver Todos" para ver todos os agendamentos atrasados e manter tudo em dia.',
    position: 'left', // balão à esquerda, com seta apontando para a direita
    isFixed: true, // não usar scrollIntoView em elementos fixed
  },
];

// Passos do tour guiado para a página Clientes
const clientesTourSteps = [
  {
    selector: '.botao-mais',
    title: 'Adicionar Cliente',
    text: 'Aqui você pode adicionar novos clientes para os seus negócios, novas oportunidades de vendas e muito mais.',
    position: 'bottom',
  },
  {
    selector: '.btn-cadastro-massa',
    title: 'Cadastro em Massa',
    text: 'Aqui você pode cadastrar em massa diversos clientes de uma só vez. Esse cadastro em massa funciona com um template Excel com layout padrão, no qual você preenche todos os dados dos clientes e, ao finalizar, ele sobe todos eles de uma vez. Você pode cadastrar vários clientes sem precisar preencher o formulário individualmente.',
    position: 'bottom',
  },
  {
    selector: '.clientes-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Listagem de Clientes',
    text: 'Aqui vai aparecer a listagem de todos os clientes que você possui. Você pode editar os dados dos clientes a qualquer momento. Clientes vão servir para fazer futuras vendas e negócios.',
    position: 'top',
  },
  {
    selector: '.filtro-container',
    title: 'Filtros',
    text: 'Aqui você pode filtrar todos os clientes, seja por nome, ID, número de celular, e-mail, CPF ou CNPJ. Seus clientes podem ser tanto empresas (pessoa jurídica) quanto pessoas físicas.',
    position: 'bottom',
  },
];

// Passos do tour guiado para a página Fornecedores
const fornecedoresTourSteps = [
  {
    selector: '.btn-adicionar-fornecedor',
    title: 'Adicionar Fornecedor',
    text: 'Aqui você cadastra novos fornecedores — sejam de materiais, produtos ou serviços. Preencha os dados como nome, CNPJ/CPF, endereço, telefone, e-mail, segmento e tipo do fornecedor.',
    position: 'bottom',
  },
  {
    selector: '.fornecedor-estatisticas',
    title: 'Estatísticas',
    text: 'Este card mostra o total de fornecedores cadastrados no sistema. O número é atualizado conforme você adiciona ou filtra fornecedores.',
    position: 'bottom',
  },
  {
    selector: '.filtro-container',
    title: 'Filtros de Busca',
    text: 'Use este campo para buscar fornecedores rapidamente. Você pode filtrar por Nome, Cidade, Segmento ou CNPJ/CPF. Clique no dropdown ao lado para escolher o critério de busca.',
    position: 'bottom',
  },
  {
    selector: '.fornecedor-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Listagem de Fornecedores',
    text: 'Aqui aparecem todos os seus fornecedores cadastrados. Em cada card você pode ver os dados principais e usar os botões "Ver Detalhes" para informações completas ou "Editar" para alterar os dados.',
    position: 'top',
  },
];

// Passos do tour guiado para a página Pessoas
const pessoasTourSteps = [
  {
    selector: '.btn-nova-pessoa',
    title: 'Nova Pessoa',
    text: 'Aqui você cadastra novas pessoas no sistema. Pessoas são os colaboradores, responsáveis e profissionais que participam dos projetos, tarefas e projetos da sua empresa. Basta informar o nome e o custo/hora.',
    position: 'bottom',
  },
  {
    selector: '.pessoas-filtro-row',
    title: 'Filtro de Busca',
    text: 'Use este campo para buscar pessoas pelo nome. Digite parte do nome e a lista será filtrada automaticamente.',
    position: 'bottom',
  },
  {
    selector: '.pessoas-estatisticas',
    title: 'Estatísticas',
    text: 'Aqui você vê o total de pessoas cadastradas e o custo/hora médio de todos os seus colaboradores. Os valores atualizam conforme você filtra a lista.',
    position: 'bottom',
  },
  {
    selector: '.pessoas-cards-container',
    fallbackSelector: '.dropdown-container',
    title: 'Listagem de Pessoas',
    text: 'Aqui aparecem todas as pessoas cadastradas. Em cada card você pode ver o nome e custo/hora, além de usar os botões "Ver Detalhes" para informações completas ou "Editar" para alterar os dados.',
    position: 'top',
  },
];

// Passos do tour guiado para a página Agendamentos
const agendamentosTourSteps = [
  {
    selector: '.botao-mais',
    title: 'Adicionar Agendamento',
    text: 'Aqui você inicia a criação de um novo agendamento. O fluxo funciona assim: primeiro escolha um cliente, depois uma oportunidade vinculada, e por fim preencha o formulário com data, horário e detalhes.',
    position: 'bottom',
  },
  {
    selector: '.filtro-container',
    title: 'Filtros',
    text: 'Use os filtros para encontrar agendamentos. Você pode filtrar por nome do cliente, ID, ID do cliente ou CPF/CNPJ. Também pode filtrar por status como "A Visitar", "Agendado", etc.',
    position: 'bottom',
  },
  {
    selector: '.filtro-atrasado-btn',
    title: 'Filtro de Atrasados',
    text: 'Clique aqui para ver apenas os agendamentos atrasados — aqueles que já passaram da data e ainda não foram concluídos. Agendamentos atrasados aparecem com um indicador vermelho no card.',
    position: 'bottom',
  },
  {
    selector: '.lista-clientes',
    fallbackSelector: '.container-geral',
    title: 'Listagem de Agendamentos',
    text: 'Aqui aparecem todos os seus agendamentos. Cada card mostra o cliente, data/hora, status e responsável. Cards com bolinha vermelha estão atrasados. Use o botão "Editar" para modificar.',
    position: 'center',
  },
  {
    selector: '.popup-agendamento',
    fallbackSelector: '.popup-agendamento-overlay',
    title: 'Formulário de Agendamento — Horários',
    text: 'No formulário, clique em um horário para definir o INÍCIO e depois em outro para definir o FIM. O bloco selecionado ficará laranja para mostrar o intervalo que você está construindo.\n\n🟠 LARANJA — Intervalo que você está selecionando para o agendamento.\n\n🔴 VERMELHO — Horários já ocupados por outros agendamentos. O sistema avisa que estão ocupados, mas não impede que você escolha o horário.\n\n🟢 VERDE — Indica o horário já definido para o agendamento atual quando você está editando um agendamento existente.',
    position: 'inner-top',
    isFixed: true,
    balloonWidth: 550,
    dispatchEvent: 'abrir-formulario-agendamento-tour',
  },
];

// Passos do tour guiado para a página Calendário
const calendarioTourSteps = [
  {
    selector: '.calendario-navegacao',
    fallbackSelector: '.calendario-container',
    title: 'Navegação do Calendário',
    text: 'Use as setas para navegar entre os meses ou selecione diretamente o mês e o ano nos seletores. Assim você pode ver os agendamentos de qualquer período.',
    position: 'bottom',
  },
  {
    selector: '.calendario-grid',
    fallbackSelector: '.calendario-container',
    title: 'Grade do Calendário',
    text: 'Cada célula representa um dia do mês. Dentro de cada dia você vê a quantidade de agendamentos e os nomes dos primeiros clientes. Passe o mouse sobre um dia para ver o botão \"Ver todos\" ou \"+\".',
    position: 'center',
  },
  {
    selector: '.popup-agendamento',
    fallbackSelector: '.popup-agendamento-overlay',
    title: 'Formulário de Agendamento — Horários',
    text: 'No formulário, clique em um horário para definir o INÍCIO e depois outro para definir o FIM. O bloco selecionado ficará laranja para mostrar o intervalo que você está construindo.\n\n🟠 LARANJA — Intervalo que você está selecionando para o agendamento.\n\n🔴 VERMELHO — Horários já ocupados por outros agendamentos. O sistema avisa que estão ocupados, mas não impede que você escolha o horário.\n\n🟢 VERDE — Indica o horário já definido para o agendamento atual quando você está editando um agendamento existente.',
    position: 'inner-top',
    isFixed: true,
    balloonWidth: 550,
    dispatchEvent: 'abrir-formulario-calendario-tour',
  },
];

// Passos do tour guiado para a página Orçamentos
const orcamentosTourSteps = [
  {
    selector: '.btn-adicionar-orcamento',
    title: 'Adicionar Orçamento',
    text: 'Clique aqui para criar um novo orçamento. O fluxo é: escolha o cliente, depois a oportunidade vinculada, e por fim preencha o formulário com os itens e valores do orçamento.',
    position: 'bottom',
  },
  {
    selector: '.orcamento-filtro-container',
    title: 'Filtro de Busca',
    text: 'Filtre os orçamentos por ID do Orçamento, ID do Cliente, Nome do Cliente, ID da Oportunidade ou Status. Clique no dropdown laranja para trocar o critério e digite no campo ao lado.',
    position: 'bottom',
  },
  {
    selector: '.orcamentos-cards-container',
    fallbackSelector: '.orcamento-header',
    title: 'Lista de Orçamentos',
    text: 'Aqui aparecem todos os orçamentos. Cada card mostra o cliente, oportunidade, valor e status com cor indicativa: 🟢 Verde = Aprovado, 🟠 Laranja = Pendente, 🔴 Vermelho = Rejeitado.\n\nCada card tem 3 ações: \"Editar\" para alterar os dados, \"Baixar PDF\" para gerar o documento, e \"Compartilhar\" para enviar o link de aprovação ao cliente via WhatsApp ou e-mail.',
    position: 'center',
  },
];

// Passos do tour guiado para a página Oportunidades
const oportunidadesTourSteps = [
  {
    selector: '.btn-nova-oportunidade',
    title: 'Nova Oportunidade',
    text: 'Clique aqui para registrar uma nova oportunidade. O sistema primeiro pede para você escolher o cliente ao qual a oportunidade será vinculada, depois abre o formulário para preencher o título, status e detalhes.',
    position: 'bottom',
  },
  {
    selector: '.filtro-container',
    title: 'Filtro de Busca',
    text: 'Filtre as oportunidades por ID da Oportunidade, ID do Cliente, Nome do Cliente, Título ou Status. Selecione o critério no dropdown laranja e digite no campo de texto — a lista atualiza automaticamente.',
    position: 'bottom',
  },
  {
    selector: '.oportunidades-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Lista de Oportunidades',
    text: 'Aqui aparecem todas as oportunidades cadastradas. Cada card mostra o ID, nome do cliente, título, status e data de criação. Use o botão \"Editar\" para alterar qualquer informação da oportunidade.',
    position: 'center',
  },
];

// Passos do tour guiado para a página Vendas de Serviços
const vendasServicosTourSteps = [
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Use este filtro para localizar vendas de serviços rapidamente. Selecione o critério no dropdown (Cliente ID, Venda ID, Orçamento ID, Valor ou Status) e digite o valor no campo de texto.',
    position: 'bottom',
  },
  {
    selector: '.oportunidade-cards',
    fallbackSelector: '.container-geral',
    title: 'Lista de Vendas',
    text: 'Aqui aparecem todas as vendas de serviços. Cada card mostra: ID da venda, ID do cliente, orçamento vinculado, CPF/CNPJ, valor final e o status com indicador de cor (🟢 Verde = Aprovada, 🟡 Amarelo = Pendente, 🔴 Vermelho = Rejeitada, 🔵 Azul = Em andamento).',
    position: 'center',
  },
  {
    selector: '.botao-editar',
    fallbackSelector: '.oportunidade-cards',
    title: 'Editar Venda',
    text: 'Clique em "Editar" em qualquer card para abrir o formulário completo da venda. Você pode alterar status, desconto, vendedor, tipo de pagamento, configuração de parcelas, impostos e dados de cartão. Atenção: vendas já aprovadas ficam bloqueadas para edição.',
    position: 'left',
  },
];

// Passos do tour guiado para a página Certificado Digital
// Passos do tour guiado para a página Produções
// Passos do tour guiado para o formulário de edição de Projeto Visual
// Tour do editor de Prompt E-mail (aberto pela página Relacionamento com Clientes).
// "acaoPromptEmail" abre o painel certo da barra da esquerda antes de destacar.
const promptEmailTourSteps = [
  {
    selector: '.prompt-email-name-input',
    title: '✏️ Nome do template',
    text: 'Comece dando um nome ao template, por exemplo "Pesquisa de satisfação" ou "Boas-vindas". É por esse nome que você vai encontrar o template depois, na hora de enviar e-mails aos clientes.',
    position: 'bottom',
    acaoPromptEmail: 'voltar-blocos',
  },
  {
    selector: '#pe-blocos',
    title: '🧱 Blocos do e-mail',
    text: 'Aqui ficam as peças do e-mail. Clique ou arraste para o centro:\n• Título — o texto de destaque.\n• Parágrafo — o texto do e-mail.\n• Links — um botão que abre um endereço.\n• Dados Clientes — campos como nome, e-mail e CPF/CNPJ, que são trocados automaticamente pelos dados de cada cliente no envio (arraste para dentro de um parágrafo).',
    position: 'right',
  },
  {
    selector: '#pe-link-texto',
    title: '🔗 Link — texto do botão',
    text: 'Ao clicar em um bloco de Link, este painel abre na esquerda. Em "Texto do botão" você escreve o nome que aparece no botão, por exemplo "Acessar proposta" ou "Falar no WhatsApp".',
    position: 'right',
    acaoPromptEmail: 'abrir-link',
  },
  {
    selector: '#pe-link-url',
    title: '🌐 Link — URL de destino',
    text: 'Em "URL de destino" vai o endereço que o botão abre quando o cliente clica, por exemplo https://seusite.com.br ou o link do seu WhatsApp.',
    position: 'right',
  },
  {
    selector: '#pe-link-cores',
    title: '🎨 Link — cores do botão',
    text: 'Personalize o botão: "Cor do botão" muda o fundo e "Cor do texto" muda a cor das letras. Use as cores da sua marca.',
    position: 'right',
  },
  {
    selector: '.prompt-email-format-toolbar',
    title: '🔤 Formatação do bloco selecionado',
    text: 'Quando você clica em um título ou parágrafo, esta barra desce no topo mostrando a formatação daquele bloco: fonte, tamanho, negrito, itálico, sublinhado e cor. O que mudar aqui vale SÓ para esse bloco (ou só para o trecho marcado). Clicando fora dos blocos, a barra recolhe.',
    position: 'bottom',
    acaoPromptEmail: 'mostrar-barra',
  },
  {
    selector: '.prompt-email-canvas-inner',
    title: '📄 Corpo do e-mail',
    text: 'Aqui é o e-mail montado, bloco por bloco. Clique em um bloco para escrever. Para reorganizar, arraste pelos pontinhos da esquerda ou use as setas ↑ ↓; o × apaga o bloco e o botão + no final adiciona um parágrafo novo.',
    position: 'left',
    acaoPromptEmail: 'voltar-blocos',
  },
  {
    selector: '#pe-globais-painel',
    title: '⚙️ Configurações Globais',
    text: 'Aqui você escolhe a fonte, o tamanho, o negrito e a cor de TODOS os blocos do e-mail de uma vez — os que já existem e os novos. O título fica sempre um pouco maior que o texto.',
    position: 'right',
    acaoPromptEmail: 'abrir-globais',
  },
  {
    selector: '#pe-globais-painel',
    title: '⚖️ Global × bloco específico',
    text: 'O que você muda num bloco pela barra de cima sempre vale mais que as Configurações Globais. Exemplo: se um parágrafo foi deixado azul pela barra de cima e depois você muda a cor global para cinza, todos os outros blocos ficam cinza e aquele parágrafo continua azul. Mudar o global nunca apaga o que você fez num bloco.',
    position: 'right',
  },
  {
    selector: '#pe-btn-carregar',
    title: '📂 Carregar template',
    text: 'Para editar um template que já existe, clique em "Carregar Template" e escolha na lista. O texto e as configurações globais voltam como foram salvos.',
    position: 'bottom',
    acaoPromptEmail: 'voltar-blocos',
  },
  {
    selector: '.prompt-email-btn-salvar',
    title: '💾 Salvar template',
    text: 'Ao terminar, clique em "Salvar Template" (ou "Atualizar Template", se estiver editando um que já existe). Depois é só escolher este template ao enviar e-mails em Relacionamento com Clientes.',
    position: 'bottom',
  },
];

const projetoVisualFormTourSteps = [
  {
    selector: '#projeto-visual-form',
    title: '📐 Formulário de Projeto Visual',
    text: 'Aqui você edita um projeto visual vinculado a um projeto. Os IDs de referência são somente leitura. Você pode alterar descrição, tipo de documento, número externo e gerenciar os arquivos (imagens e PDFs) associados ao projeto.',
    position: 'right',
  },
  {
    selector: '#projeto-visual-secao-refs',
    title: '🔗 Referências do Projeto',
    text: 'IDs de referência são somente leitura: ID do projeto, Cliente, Oportunidade, Contrato e Projeto vinculado. Esses vínculos são definidos automaticamente pelo sistema.',
    position: 'right',
  },
  {
    selector: '#projeto-visual-secao-dados',
    title: '✏️ Dados Editáveis',
    text: 'Edite a descrição do projeto, o tipo de documento (DWG, PDF, PNG, JPG, SKP, RVT ou Outros) e o número do projeto externo (referência no sistema de origem, ex: CAD-2025-001).',
    position: 'right',
  },
  {
    selector: '#projeto-visual-secao-uploads',
    title: '📂 Upload de Arquivos',
    text: 'Selecione imagens (PNG, JPG) e/ou PDFs para enviar ao projeto. Os arquivos são enviados ao servidor ao clicar em “Salvar Alterações”.',
    position: 'top',
  },
  {
    selector: '#projeto-visual-secao-arquivos-salvos',
    title: '🖼️ Arquivos Já Enviados',
    text: 'Aqui aparecem as imagens e PDFs já salvos no servidor para este projeto. Clique em qualquer arquivo para ver as opções de Baixar ou Deletar.',
    position: 'top',
  },
];

// Passos do tour guiado para a página Projeto Visual
const projetoVisualTourSteps = [
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Use este filtro para localizar projetos visuais por diferentes critérios. Selecione o campo no dropdown e digite o valor para filtrar a lista.',
    position: 'bottom',
  },
  {
    selector: '.projeto-visual-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Lista de Projetos Visuais',
    text: 'Cada card mostra: ID, Projeto vinculado, Cliente, Oportunidade, Contrato, Número Externo e Tipo de Documento (DWG, PDF, PNG, JPG, SKP, RVT). Use "Ver Detalhes" para visualizar ou "Editar" para alterar dados e gerenciar arquivos.',
    position: 'center',
  },
  {
    selector: '.btn-editar-projeto-visual',
    fallbackSelector: '.projeto-visual-cards-container',
    title: 'Editar Projeto Visual',
    text: 'Clique em "Editar" para abrir o formulário. Você pode alterar descrição, tipo de documento e número externo, além de fazer upload de novas imagens e PDFs ou baixar e excluir arquivos existentes.',
    position: 'left',
  },
];

// Passos do tour guiado para a página Matéria-Prima
const materiaPrimaTourSteps = [
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Buscar Projeto',
    text: 'Digite e escolha o campo ao lado: Todos os campos, Projeto ID, Orçamento ID ou Nome do cliente.',
    position: 'bottom',
  },
  {
    selector: '.materia-prima-producoes-grid',
    fallbackSelector: '.container-geral',
    title: 'Projetos',
    text: 'Clique numa projeto para ver as matérias-primas dela:\n• Reservadas no orçamento: tudo o que o orçamento reservou no início (o previsto)\n• Usadas no projeto: o que foi alocado nas tarefas, garantias e manutenções dela\n• Perdidas no projeto: o que se perdeu ou danificou no caminho\n• Total: usado + perdido + reservado restante, por material. As tarefas gastam primeiro o reservado e, quando ele acaba, o estoque.\nCom o pop-up aberto, use "Resumo deste formulário" para a explicação de cada parte.',
    position: 'top',
  },
];

// Passos do pop-up de uma produção na tela Matéria-Prima (os que não existirem no pop-up aberto são pulados)
const materiaPrimaFormTourSteps = [
  {
    selector: '#mp-resumo-reservadas',
    fallbackSelector: '.materia-prima-resumo-modal',
    title: '📌 Reservadas no orçamento',
    text: 'É o que o ORÇAMENTO reservou de matéria-prima no início (o previsto). Ao alocar no orçamento, o material sai do Estoque de Materiais na hora e fica guardado para esse cliente.\n\nEsta parte é FIXA: mostra sempre o que foi reservado inicialmente, mesmo depois que o projeto começa a usar.',
    position: 'left',
  },
  {
    selector: '#mp-resumo-usadas',
    fallbackSelector: '.materia-prima-resumo-modal',
    title: '🔨 Usadas no projeto',
    text: 'É o que foi realmente usado: a matéria-prima alocada nas TAREFAS do projeto (e nas garantias/manutenções dela). A coluna "Onde" mostra em qual tarefa foi usada.\n\nCada uso tira primeiro do que o orçamento reservou; quando a reserva acaba, sai do Estoque de Materiais. Matéria-prima só entra no projeto pelas tarefas.',
    position: 'left',
  },
  {
    selector: '#mp-resumo-perdidas',
    fallbackSelector: '.materia-prima-resumo-modal',
    title: '⚠️ Perdidas no projeto',
    text: 'É o que foi alocado para este projeto, numa tarefa, mas se perdeu no caminho ou foi danificado (quebrou, estragou, sumiu). Não virou produto e também não voltou ao estoque.\n\nÉ informado na tarefa: ao diminuir a quantidade, ao concluir ou ao cancelar a tarefa. O valor perdido entra no custo do projeto.',
    position: 'left',
  },
  {
    selector: '#mp-resumo-total',
    fallbackSelector: '.materia-prima-resumo-modal',
    title: '📊 Total — o mais importante',
    text: 'Por material, junta tudo:\n• Usado: o que virou produto\n• Perdido: o que se perdeu (funciona como um "parceiro" do usado: também foi gasto e também tira do reservado)\n• Reservado restante: o que ainda sobra da reserva\n• Total = Usado + Perdido + Reservado restante\n\nExemplo: reservou 30, usou 10 e perdeu 5 → Usado 10, Perdido 5, Reservado restante 15, Total 30. Se usado + perdido passar do reservado, aparece em vermelho "passou X do reservado" (o excedente saiu do estoque).',
    position: 'top',
  },
];

// Passos do tour guiado para o formulário de Tarefas do Projeto
const tarefasFormTourSteps = [
  {
    selector: '#tarefa-form-dados',
    title: '📋 Dados da Tarefa',
    text: 'Preencha a descrição, o status (Pendente, Em Andamento, Concluído, Atrasado ou Cancelado) e o tipo de tarefa. O tipo pode ser criado ou gerenciado pelo botão "+".',
    position: 'right',
  },
  {
    selector: '#tarefa-form-responsaveis',
    title: '👤 Responsáveis (Criação)',
    text: 'Selecione um ou mais responsáveis pela tarefa e informe as horas reais de cada um. Use o botão "+" para adicionar mais responsáveis.',
    position: 'right',
  },
  {
    selector: '#tarefa-form-materias',
    title: '🧱 Matérias-Primas',
    text: 'Vincule matérias-primas necessárias para a execução desta tarefa. As alocações são salvas ao confirmar o formulário.',
    position: 'top',
  },
  {
    selector: '#tarefa-form-fotos',
    title: '📷 Fotos da Tarefa',
    text: 'No modo de edição, é possível visualizar as fotos salvas e enviar novas imagens para documentar o progresso ou resultado da tarefa.',
    position: 'top',
  },
  {
    selector: '#tarefa-form-responsaveis-vinculados',
    title: '👥 Responsáveis Vinculados',
    text: 'Veja todos os responsáveis associados a esta tarefa com custo/hora, horas previstas e horas reais. Na edição, é possível adicionar novos responsáveis.',
    position: 'top',
  },
];

// Passos do tour guiado para a página Tarefas do Projeto
const tarefasProjetoTourSteps = [
  {
    selector: '.container-geral',
    fallbackSelector: '.container-geral',
    title: 'Adicionar Tarefa',
    text: 'Clique em "+ Adicionar Tarefa" para criar uma nova tarefa. Primeiro escolha o projeto vinculado no seletor, depois preencha o formulário com descrição, datas, tipo, responsáveis e horas previstas.',
    position: 'center',
  },
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Filtre as tarefas por Descrição, Status, Responsável ou ID de Projeto para localizar rapidamente o que precisa.',
    position: 'bottom',
  },
  {
    selector: '.tarefas-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Lista de Tarefas',
    text: 'Cada card mostra descrição, projeto, cliente, datas, horas previstas/reais, tipo e status com badge colorido: 🟢 Concluída, 🔵 Em Andamento, 🟡 Pendente, 🔴 Atrasada, ⚪ Cancelada.',
    position: 'center',
  },
  {
    selector: '.btn-editar-tarefa',
    fallbackSelector: '.tarefas-cards-container',
    title: 'Editar Tarefa',
    text: 'Clique em "Editar" para abrir o formulário completo da tarefa. Você pode alterar todos os campos, adicionar ou remover responsáveis e ajustar horas. As horas reais alimentam automaticamente o custo acumulado do projeto.',
    position: 'left',
  },
];

const producoesTourSteps = [
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Use este filtro para localizar projetos. Selecione o critério no dropdown (ID, Cliente, Status, Status de Custo, Título ou Contrato) e digite o valor no campo de texto.',
    position: 'bottom',
  },
  {
    selector: '.oportunidade-cards',
    fallbackSelector: '.container-geral',
    title: 'Lista de Projetos',
    text: 'Cada card mostra: ID, título, cliente, contrato, orçamento, custo orçado, custo acumulado, horas previstas/reais, status de custo (com cor) e as datas previstas e reais. O custo acumulado é calculado automaticamente pelas horas trabalhadas, matéria-prima e serviços terceirizados.',
    position: 'center',
  },
  {
    selector: '.botao-editar',
    fallbackSelector: '.oportunidade-cards',
    title: 'Editar Projeto',
    text: 'Clique em "Editar" para abrir o formulário completo. Lá você gerencia tarefas, responsáveis, matéria-prima consumida e serviços terceirizados. O projeto é finalizado quando o cliente aprova pelo link enviado daqui; a sobra de material volta ao estoque nessa hora.',
    position: 'left',
  },
];

// Passos do tour guiado para a página Serviços Terceiros
const servicosTerceirosTourSteps = [
  {
    selector: '.dropdown-container',
    fallbackSelector: '.dropdown-container',
    title: 'Adicionar Serviço',
    text: 'Clique em "Adicionar" para registrar um novo serviço terceirizado. Primeiro escolha o projeto vinculado, depois preencha o formulário com fornecedor, tipo, valor, data, status e descrição.',
    position: 'center',
  },
  {
    selector: '.dropdown-filter-row',
    fallbackSelector: '.dropdown-container',
    title: 'Filtro de Busca',
    text: 'Filtre os serviços por Descrição, Status, Fornecedor ou ID de Projeto. A barra laranja abaixo mostra o total de serviços e o valor total filtrados.',
    position: 'bottom',
  },
  {
    selector: '.servicos-terceiros-cards-container',
    fallbackSelector: '.dropdown-container',
    title: 'Lista de Serviços',
    text: 'Cada card mostra descrição, projeto, fornecedor, tipo, valor e data. O status é indicado por cor na borda e badge: 🟢 Concluído, 🔵 Em Andamento, 🟠 Pendente, 🔴 Cancelado.',
    position: 'center',
  },
  {
    selector: '.btn-editar-servico-terceiro',
    fallbackSelector: '.servicos-terceiros-cards-container',
    title: 'Editar Serviço',
    text: 'Clique em "Editar" para abrir o formulário completo. Você pode alterar fornecedor, tipo de serviço, valor, data de execução, status e descrição. O valor alimenta o custo acumulado do projeto automaticamente.',
    position: 'left',
  },
];

const certificadoTourSteps = [
  {
    selector: '.container-geral',
    title: 'Certificado Digital A1',
    text: 'O Certificado Digital A1 (.pfx / .p12) é obrigatório para emitir notas fiscais no Brasil. Sem ele o sistema não consegue se comunicar com a SEFAZ. Aqui você cadastra, renova e gerencia os certificados vinculados à sua empresa.',
    position: 'center',
  },
  {
    selector: '.form-group',
    fallbackSelector: '.container-geral',
    title: 'Cadastrar Novo Certificado',
    text: 'Clique em "+ Novo Certificado" para abrir o formulário. Faça upload do arquivo .pfx ou .p12 (arraste e solte ou clique na área), informe a senha do certificado e opcionalmente um e-mail para alertas de vencimento. O CNPJ da empresa é preenchido automaticamente.',
    position: 'bottom',
  },
];

// Passos do tour guiado para a página Notas Fiscais
const notaFiscalTourSteps = [
  {
    selector: '.container-geral',
    title: 'Notas de Produtos ou Serviços',
    text: 'Use os botões no topo para alternar entre "Notas de Produtos" (NF-e) e "Notas de Serviços" (NFS-e). As abas abaixo separam as notas em "Pendentes" (aguardando autorização) e "Emitidas" (já autorizadas pela SEFAZ).',
    position: 'center',
  },
  {
    selector: '.modal-overlay',
    fallbackSelector: '.container-geral',
    title: 'Detalhes da Nota',
    text: 'Clique em qualquer linha da lista para abrir o painel completo da nota: dados fiscais, chave de acesso, protocolo de autorização, ambiente (Produção ou Homologação), itens da venda, dados do comprador e download do PDF.',
    position: 'center',
  },
];

// Passos do tour guiado para a página Contas a Receber
const contasReceberTourSteps = [
  {
    selector: '.contas-receber-filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Use este filtro para localizar contas rapidamente. Selecione o critério no dropdown (Cliente, Valor, Data de Vencimento ou Status) e digite o valor no campo de texto.',
    position: 'bottom',
  },
  {
    selector: '.contas-receber-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Lista de Contas',
    text: 'Aqui aparecem as contas a receber da aba selecionada. Cada card mostra cliente, valor em destaque, vencimento, parcelas e juros. O status indica: 🟢 Pago, 🟠 Pendente ou 🔴 Vencido.',
    position: 'center',
  },
  {
    selector: '.btn-abrir-conta',
    fallbackSelector: '.contas-receber-cards-container',
    title: 'Abrir Conta',
    text: 'Clique em "Abrir Conta" para ver todos os detalhes da conta: dados completos, configuração de pagamento e simulação das parcelas com juros calculados automaticamente (simples ou compostos).',
    position: 'left',
  },
];

// Passos do tour guiado para a página Contas a Pagar
const contasPagarTourSteps = [
  {
    selector: '.container-geral',
    title: 'Contas a Pagar',
    text: 'Nesta página você controla todas as obrigações financeiras da empresa: despesas, compras e pagamentos a fornecedores. Use o filtro no topo para localizar contas por Fornecedor, Valor, Data de Vencimento ou Status.',
    position: 'center',
  },
  {
    selector: '.oportunidade-cards',
    fallbackSelector: '.container-geral',
    title: 'Lista de Contas',
    text: 'Aqui aparecem todas as contas a pagar. Cada item mostra fornecedor, valor, data de vencimento e status. O status indica visualmente se a conta está 🟢 Paga, 🟡 Pendente ou 🔴 Vencida.',
    position: 'center',
  },
];

// Passos do tour guiado para o formulário de contrato (edição)
const contratoFormTourSteps = [
  {
    selector: '#contrato-form-geral',
    title: '📄 Formulário do Contrato',
    text: 'Aqui você edita todas as informações do contrato: IDs de referência (Orçamento, Cliente, Venda), status, valor, datas, prazo de garantia, descrição, condições de pagamento, serviços contratados e aditivos contratuais.',
    position: 'right',
  },
  {
    selector: '#contrato-secao-status',
    title: '📊 Status e Valor',
    text: 'Defina o status: "Pendente Assinatura", "Assinado" ou "Cancelado". Ao alterar para "Assinado", será obrigatório fazer upload do PDF do contrato assinado pelo cliente.',
    position: 'right',
  },
  {
    selector: '#contrato-secao-datas',
    title: '📅 Datas do Contrato',
    text: 'Registre as datas importantes: Data de Assinatura (quando o contrato foi assinado), Data de Vigência (quando expira) e o Prazo de Garantia em meses.',
    position: 'right',
  },
  {
    selector: '#contrato-secao-textos',
    title: '📝 Descrição e Condições',
    text: 'Preencha a descrição do orçamento, as condições de pagamento acordadas e os serviços contratados. Essas informações compõem o corpo do contrato.',
    position: 'right',
  },
  {
    selector: '#contrato-secao-aditivos',
    title: '📋 Aditivos Contratuais',
    text: (
      <div>
        <p>Os aditivos contratuais permitem registrar alterações formais no contrato sem criar um novo. Aparecem <strong>somente depois que o contrato foi assinado</strong>.</p>
        <p>• <strong>Antes da assinatura:</strong> o campo não aparece; corrija o próprio contrato e salve.</p>
        <p>Tipos: <strong>Valor</strong> (acréscimo/desconto), <strong>Prazo</strong> (dias adicionais), <strong>Escopo</strong> (alteração nos serviços) ou <strong>Misto</strong>.</p>
      </div>
    ),
    position: 'top',
  },
];

// Passos do tour guiado para o formulário de Venda de Produto
const vendasProdutosFormTourSteps = [
  {
    selector: '#venda-prod-form',
    title: '🛒 Formulário de Venda de Produto',
    text: 'Aqui você registra ou edita uma venda de produto. Preencha os dados do cliente, selecione os produtos, informe quantidades, defina o status e configure o pagamento.',
    position: 'right',
  },
  {
    selector: '#venda-prod-secao-produto',
    title: '📦 Produto(s) da Venda',
    text: 'O produto selecionado aparece aqui com nome, ID, estoque disponível e preço. Em vendas com múltiplos itens, todos são listados abaixo com quantidade e valor unitário — você pode editar ou remover cada item individualmente.',
    position: 'right',
  },
  {
    selector: '#venda-prod-secao-comprador',
    title: '🧾 Dados do Comprador (NF-e)',
    text: 'Esta seção é opcional para salvar a venda, mas obrigatória para emitir Nota Fiscal. Preencha CPF ou CNPJ, nome/razão social, endereço completo, código IBGE do município, dados fiscais (Indicador IE, inscrição estadual, regime tributário) e presença do comprador. Ao selecionar um cliente, os campos são preenchidos automaticamente a partir do cadastro. Você pode completar ou revisar depois na página de Nota Fiscal.',
    position: 'top',
  },
  {
    selector: '#venda-prod-secao-pagamento',
    title: '💳 Status e Pagamento',
    text: 'Defina o status da venda (Pendente, Aprovada, Rejeitada ou Cancelada) e selecione o tipo de pagamento. Após escolher o tipo, aparece a seção de configuração de parcelas e juros. Atenção: vendas já Aprovadas ficam bloqueadas para edição.',
    position: 'top',
  },
];

// Passos do tour guiado para o formulário de Venda de Serviço
const vendasServicosFormTourSteps = [
  {
    selector: '#venda-serv-form',
    title: '📋 Formulário de Venda de Serviço',
    text: 'Aqui você edita uma venda de serviço. Os campos de Cliente ID e Orçamento ID são somente leitura (preenchidos a partir do orçamento vinculado). Preencha os demais campos conforme necessário.',
    position: 'right',
  },
  {
    selector: '#venda-serv-secao-geral',
    title: '📌 Referências',
    text: 'Cliente ID e Orçamento ID são preenchidos automaticamente a partir do orçamento vinculado e não podem ser editados.',
    position: 'right',
  },
  {
    selector: '#venda-serv-secao-valores',
    title: '💰 Valores',
    text: 'O Valor do Orçamento é somente leitura. Informe um desconto se houver — o Valor Final da Venda é calculado automaticamente (Valor Orçamento − Desconto).',
    position: 'right',
  },
  {
    selector: '#venda-serv-secao-status',
    title: '📊 Status e Vendedor',
    text: 'Defina o status da venda: Pendente, Aprovada, Rejeitada ou Cancelada. Ao aprovar, contas a receber e contrato são gerados automaticamente. Selecione também o vendedor responsável.',
    position: 'right',
  },
  {
    selector: '#venda-serv-secao-pagamento',
    title: '💳 Tipo de Pagamento',
    text: 'Selecione o tipo de pagamento. Após a seleção aparece a seção de configuração de parcelas, juros e dados de cartão (quando aplicável). Vendas aprovadas ficam bloqueadas para edição.',
    position: 'top',
  },
];

// Passos do tour guiado para a página Contratos
const contratosTourSteps = [
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Use este filtro para localizar contratos rapidamente. Selecione o critério no dropdown (ID do Contrato, ID do Cliente, ID do Orçamento, Status ou Valor) e digite o valor no campo de texto.',
    position: 'bottom',
  },
  {
    selector: '.lista-clientes',
    fallbackSelector: '.container-geral',
    title: 'Lista de Contratos',
    text: 'Aqui aparecem todos os contratos. Cada card mostra: ID, nome do cliente, título da oportunidade, IDs vinculados, valor e status. O status é exibido em 🟢 verde quando "Assinado" e em 🔴 vermelho para os demais estados.',
    position: 'center',
  },
  {
    selector: '.btn-baixar-contrato',
    fallbackSelector: '.contrato-card',
    title: 'Baixar PDF',
    text: 'Clique em "Baixar PDF" para gerar e baixar o documento do contrato em formato PDF diretamente.',
    position: 'left',
  },
  {
    selector: '.btn-editar-contrato',
    fallbackSelector: '.contrato-card',
    title: 'Editar Contrato',
    text: 'Clique em "Editar" para abrir o formulário completo do contrato. Você pode alterar status, valor, datas, vigência, prazo de garantia, descrição e condições de pagamento. Também é possível fazer upload do PDF assinado e gerenciar Aditivos Contratuais (de valor, prazo ou escopo).',
    position: 'left',
  },
];

// Passos do tour guiado para a página Vendas de Produtos
const vendasProdutosTourSteps = [
  {
    selector: '.btn-nova-venda-produto',
    title: 'Nova Venda',
    text: 'Clique aqui para registrar uma nova venda de produtos. Você seleciona o cliente, escolhe os produtos do catálogo (o sistema verifica o estoque automaticamente), define a quantidade e os valores.',
    position: 'bottom',
  },
  {
    selector: '.btn-importar-produtos',
    fallbackSelector: '.vendas-header',
    title: 'Importar Produtos',
    text: 'Use este botão para importar produtos em lote a partir de um arquivo, agilizando o cadastro de muitos itens de uma só vez.',
    position: 'bottom',
  },
  {
    selector: '.vendas-filtro-container',
    title: 'Filtro de Busca',
    text: 'Filtre as vendas por ID da Venda, ID do Cliente, Nome do Cliente, ID do Produto ou Nome do Produto. Selecione o critério no dropdown laranja e digite no campo ao lado.',
    position: 'bottom',
  },
  {
    selector: '.lista-clientes',
    fallbackSelector: '.vendas-header',
    title: 'Lista de Vendas',
    text: 'Aqui aparecem todas as vendas de produtos. Cada card mostra o cliente, produto, quantidade, valor e status com cores: 🟢 Verde = Aprovada, 🟠 Laranja = Pendente, 🔴 Vermelho = Rejeitada. Use o botão \"Editar\" para modificar qualquer venda.',
    position: 'center',
  },
];

// Passos do tour guiado para o formulário de Garantia
const garantiaFormTourSteps = [
  {
    selector: '#garantia-form-dados',
    title: '🛡️ Dados da Garantia',
    text: 'Preencha Projeto, Cliente, Data de Solicitação, Status, Custo, Pessoa responsável e a Descrição do Problema.',
    position: 'right',
  },
  {
    selector: '#garantia-form-responsaveis',
    title: '👤 Responsáveis',
    text: 'Selecione responsáveis e informe horas previstas (criação) ou horas reais (edição). Use "+" para adicionar mais linhas.',
    position: 'right',
  },
  {
    selector: '#garantia-form-responsaveis-vinculados',
    title: '👥 Responsáveis Vinculados',
    text: 'No modo edição, exibe os responsáveis já vinculados com custo/hora e horas. Use "+ Adicionar" para incluir novos.',
    position: 'top',
  },
  {
    selector: '#garantia-form-materias',
    title: '🧱 Matérias-Primas',
    text: 'Vincule matérias-primas necessárias para execução desta garantia. As alocações são salvas ao confirmar o formulário.',
    position: 'top',
  },
];

// Passos do tour guiado para a página Garantias
const garantiaTourSteps = [
  {
    selector: '.btn-adicionar',
    fallbackSelector: '.container-geral',
    title: 'Adicionar Garantia',
    text: 'Clique em "Adicionar Garantia" para registrar uma nova garantia. Primeiro escolha o projeto vinculado (com filtro), depois preencha o formulário com descrição do problema, custo, responsáveis e matéria-prima.',
    position: 'bottom',
  },
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Use este filtro para localizar garantias rapidamente. Selecione o critério no dropdown laranja (ID, Projeto, Cliente, Contrato, Status ou Data) e digite o valor no campo de texto.',
    position: 'bottom',
  },
  {
    selector: '.garantia-cards-container',
    fallbackSelector: '.lista-entregas',
    title: 'Lista de Garantias',
    text: 'Aqui aparecem todas as garantias em formato de cards. Cada card mostra o contrato, projeto, cliente, data, custo e descrição do problema. O badge colorido indica o status: 🟠 Solicitada, 🔵 Em Análise, 🟢 Aprovada, 🟣 Em Andamento, 🟢 Concluída, 🔴 Recusada, ⚫ Cancelada.',
    position: 'center',
  },
  {
    selector: '.btn-editar-garantia',
    fallbackSelector: '.garantia-cards-container',
    title: 'Editar Garantia',
    text: 'Clique em "Editar" para abrir o formulário completo da garantia com todos os dados carregados. Você pode alterar status, custo, descrição, responsáveis e matéria-prima alocada.',
    position: 'left',
  },
];

// Passos do tour guiado para o formulário de Manutenção
const manutencaoFormTourSteps = [
  {
    selector: '#manutencao-form-dados',
    title: '🔧 Dados da Manutenção',
    text: 'Preencha o ID de Projeto, ID do cliente, data de execução, custo e a descrição do serviço de manutenção.',
    position: 'right',
  },
  {
    selector: '#manutencao-form-responsaveis',
    title: '👤 Responsáveis',
    text: 'Selecione um ou mais responsáveis e informe as horas previstas (na criação) ou horas reais (na edição). Use o botão "+" para adicionar mais responsáveis.',
    position: 'right',
  },
  {
    selector: '#manutencao-form-responsaveis-vinculados',
    title: '👥 Responsáveis Vinculados',
    text: 'No modo de edição, exibe os responsáveis já vinculados com custo/hora e horas trabalhadas. Use "+ Adicionar" para incluir novos.',
    position: 'top',
  },
  {
    selector: '#manutencao-form-materias',
    title: '🧱 Matérias-Primas',
    text: 'Vincule matérias-primas necessárias para a execução desta manutenção. As alocações são salvas ao confirmar o formulário.',
    position: 'top',
  },
];

// Passos do tour guiado para a página Manutenção
const manutencaoTourSteps = [
  {
    selector: '.btn-adicionar',
    fallbackSelector: '.container-geral',
    title: 'Adicionar Manutenção',
    text: 'Clique em "Adicionar Manutenção" para registrar um novo serviço de manutenção. Primeiro escolha o projeto vinculado (com filtro), depois preencha o formulário com descrição, custo, responsável e matéria-prima.',
    position: 'bottom',
  },
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Filtre as manutenções por ID, Projeto, Cliente, Responsável ou Data. Selecione o critério no dropdown laranja e digite o valor no campo ao lado.',
    position: 'bottom',
  },
  {
    selector: '.manutencao-cards-container',
    fallbackSelector: '.lista-entregas',
    title: 'Lista de Manutenções',
    text: 'Cada card mostra o projeto vinculado, cliente, responsável, data de execução, custo e descrição do serviço. Use o calendário (📅) para visualizar as manutenções por dia com indicadores de atraso.',
    position: 'center',
  },
  {
    selector: '.btn-editar-manutencao',
    fallbackSelector: '.manutencao-cards-container',
    title: 'Editar Manutenção',
    text: 'Clique em "Editar" para abrir o formulário com os dados da manutenção. Altere descrição, custo, responsável, data de execução e a alocação de matéria-prima.',
    position: 'left',
  },
];

// Passos do tour guiado para a página Relacionamento com Clientes
const relacionamentoClientesTourSteps = [
  {
    selector: '.container-geral',
    fallbackSelector: '.container-geral',
    title: 'Adicionar Relacionamento',
    text: 'Clique em "+ Adicionar Relacionamento" para registrar uma nova ação pós-venda. Escolha o cliente, o tipo de evento (e-mail, ligação, pesquisa, visita, etc.), a data e o status.',
    position: 'center',
  },
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro e Estatísticas',
    text: 'Use o filtro para buscar por ID, Cliente, Tipo de Evento, Status ou Data. Logo abaixo, os 3 contadores mostram quantas ações estão Agendadas (azul), Em Andamento (laranja) e Concluídas (verde).',
    position: 'bottom',
  },
  {
    selector: '.relacionamento-cards-container',
    fallbackSelector: '.lista-entregas',
    title: 'Lista de Relacionamentos',
    text: 'Cada card mostra o tipo de evento com ícone, cliente, data, e-mail e celular. A cor da borda e do badge indica o status. Use o botão "Editar" para atualizar o status ou reagendar.',
    position: 'center',
  },
];

// Passos do tour guiado para o formulário de Produto
const produtoFormTourSteps = [
  {
    selector: '#produto-form-dados',
    fallbackSelector: '.popup-agendamento',
    title: 'Dados Gerais',
    text: 'Preencha o nome, descrição, categoria e preço do produto. Esses são os campos básicos para identificação.',
    position: 'right',
  },
  {
    selector: '#produto-form-estoque',
    fallbackSelector: '.popup-agendamento',
    title: 'Controle de Estoque',
    text: 'Informe o estoque físico disponível, o estoque reservado e o estoque mínimo para alertas de reposição.',
    position: 'right',
  },
  {
    selector: '#produto-form-fiscal',
    fallbackSelector: '.popup-agendamento',
    title: 'Dados Fiscais (NF-e)',
    text: 'Preencha os campos obrigatórios para emissão de NF-e: NCM, CFOP, origem da mercadoria, regime tributário e CST. Essenciais para a nota fiscal.',
    position: 'left',
  },
  {
    selector: '#produto-form-medidas',
    fallbackSelector: '.popup-agendamento',
    title: 'Peso e Medidas',
    text: 'Informe o peso líquido e bruto do produto. Se necessário, adicione as dimensões (comprimento, largura, altura) para cálculo de frete.',
    position: 'left',
  },
];

// Passos do tour guiado para a página Produtos
const produtosTourSteps = [
  {
    selector: '.container-geral',
    fallbackSelector: '.container-geral',
    title: 'Adicionar Produto',
    text: 'Clique em "Adicionar Produto" para abrir o formulário completo. Preencha os dados gerais (nome, preço, categoria), controle de estoque (físico, reservado, mínimo) e os dados fiscais obrigatórios para emissão de NF-e (NCM, CFOP, CST, etc.).',
    position: 'center',
  },
  {
    selector: '.filtro-container-produto',
    fallbackSelector: '.container-geral',
    title: 'Filtro e Categorias',
    text: 'Filtre os produtos por ID, Nome ou Título. Na borda direita da tela, clique em "CATEGORIAS" para abrir o painel lateral onde você gerencia as categorias dos produtos.',
    position: 'bottom',
  },
  {
    selector: '.produto-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Lista de Produtos',
    text: 'Cada card mostra nome, categoria, preço, estoque disponível e status (🟢 Ativo / 🔴 Inativo). Clique em "Editar" para alterar os dados. Use "📥 Cadastro em Massa" para importar vários produtos de uma planilha Excel.',
    position: 'center',
  },
];

// Passos do tour guiado para a página Estoque de Materiais
const estoqueMaterialTourSteps = [
  {
    selector: '.container-geral',
    fallbackSelector: '.container-geral',
    title: 'Adicionar Material',
    text: 'Clique em "Adicionar Material" para cadastrar um novo insumo. Preencha título, tipo, unidade de medida, quantidade, custo unitário e fornecedor.',
    position: 'center',
  },
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Filtre os materiais por Título, Tipo ou Fornecedor usando o dropdown ao lado do campo de texto.',
    position: 'bottom',
  },
  {
    selector: '.estoque-material-cards-container',
    fallbackSelector: '.container-geral',
    title: 'Lista de Materiais',
    text: 'Cada card mostra título, tipo, unidade, quantidade, custo unitário e fornecedor. Use "Editar" para alterar os dados ou "Excluir" para remover o material do estoque.',
    position: 'center',
  },
];

// Passos do tour guiado para a página Entregas de Produtos
const entregasProdutosTourSteps = [
  {
    selector: '.entrega-aviso-automatica',
    fallbackSelector: '.container-geral',
    title: 'Como funcionam as entregas',
    text: 'A primeira entrega nasce quando a venda de produto é aprovada. Para entregar em partes, diminua a quantidade dela e agende outra entrega para a mesma venda.',
    position: 'bottom',
  },
  {
    selector: '.filtro-container',
    fallbackSelector: '.container-geral',
    title: 'Filtro de Busca',
    text: 'Filtre as entregas por ID da Entrega, ID do Cliente, Data ou Nome do Cliente. Selecione o critério no dropdown laranja e digite no campo ao lado.',
    position: 'bottom',
  },
  {
    selector: '.entregas-container',
    fallbackSelector: '.lista-entregas',
    title: 'Lista de Entregas',
    text: 'Cada card mostra o cliente, data programada, status, local e observações. Clique em "✅ Concluir" para registrar a entrega com a foto obrigatória, ou em "✏️ Editar" para alterar os dados. Use o calendário (📅) para ver as entregas por dia.',
    position: 'center',
  },
];

// Passos do tour guiado para a página Entregas de Serviços

// Passos do tour guiado para a página Conta de Recebimento
const cadastrarContaTourSteps = [
  {
    selector: '.minha-conta-card',
    title: '🏦 Conta de Recebimento',
    text: 'Aqui é onde você cadastra o banco para receber por todas as suas vendas. Seja de produtos, seja de serviços — qualquer venda gerada dentro da plataforma passa por aqui.\n\nCadastre a sua conta bancária para poder receber os pagamentos diretamente na sua conta.',
    position: 'center',
  },
  {
    selector: '.minha-conta-card',
    title: '📝 Etapas do Cadastro',
    text: 'O formulário de cadastro é dividido em 3 etapas, indicadas pelas 3 bolinhas numeradas no topo:\n\n● Etapa 1 — Dados Pessoais: nome, CPF ou CNPJ, e-mail, telefone e data de nascimento (para PJ: dados da empresa e do representante legal)\n\n● Etapa 2 — Endereço: rua, cidade, estado e CEP completos\n\n● Etapa 3 — Conta Bancária: selecione o banco, informe agência, número da conta e tipo (corrente ou poupança)',
    position: 'center',
  },
  {
    selector: '.minha-conta-card',
    title: '✅ Verificação da Conta',
    text: 'Após o cadastro, sua conta passa por um processo de verificação em 3 etapas:\n\n● 1 — Conta criada: seus dados foram recebidos e registrados na plataforma\n\n● 2 — Verificação pendente: documentos em análise. Pode ser necessário enviar documentos de identidade clicando em "Verificar Identidade"\n\n● 3 — Conta ativa: verificação concluída! Pagamentos e saques estão habilitados e você já pode receber via PIX, cartão ou boleto',
    position: 'center',
  },
];

// Passos do tour guiado para a página Cadastrar Funcionários
const cadastrarFuncionariosTourSteps = [
  {
    selector: '.cadastrar-usuario-card',
    title: 'Cadastrar Funcionários',
    text: 'Aqui você pode cadastrar novos funcionários para a sua conta.\n\nA quantidade de funcionários que você pode cadastrar é de acordo com o plano que você tem. Você pode visualizar seu plano ativo em Configurações → Meus Planos.\n\nCaso tenha excedido o limite, você pode comprar acesso para um novo usuário à parte ou fazer um upgrade do plano, acessando Configurações → Meus Planos.',
    position: 'center',
  },
  {
    selector: '.btn-adicionar-conta',
    title: 'Adicionar Novo Usuário',
    text: 'Clique aqui para adicionar novos usuários à sua conta. O formulário de cadastro será aberto com campos de login, senha, CPF, cargo, nível e dados pessoais.\n\nAo selecionar o nível do funcionário, a seção de Permissões de Acesso aparece automaticamente no final do formulário.',
    position: 'bottom',
    dispatchEvent: 'tour-open-popup-cadastrar',
  },
  {
    selector: '.permissoes-grid',
    fallbackSelector: '.popup-content',
    title: '🔐 Permissões de Acesso',
    text: 'Aqui você pode selecionar e controlar os acessos dos funcionários. Os acessos dependem do nível do funcionário:\n\n• Supervisor, Gerente, Diretor, TI — cada nível tem um conjunto padrão de permissões.\n\nAlém disso, você pode liberar ou bloquear manualmente cada página que o funcionário terá acesso, marcando ou desmarcando a caixinha ao lado de cada módulo e depois salvando.',
    position: 'right',
    balloonWidth: 280,
    dispatchEvent: 'tour-set-nivel-gerente',
  },
];

// Passos do tour guiado para a página Gerenciar Funcionários
const gerenciarFuncionariosTourSteps = [
  {
    selector: '.cadastrar-usuario-card',
    title: 'Gerenciar Funcionários',
    text: 'Aqui você edita e gerencia todas as contas de funcionários vinculadas à sua empresa. Cada funcionário aparece em um card com seus dados principais.\n\nUse o botão "Cadastrar Novo Funcionário" para adicionar novos funcionários à sua conta.',
    position: 'center',
  },
  {
    selector: '.funcionarios-section',
    fallbackSelector: '.cadastrar-usuario-card',
    title: 'Editando um Funcionário',
    text: 'Em cada card de funcionário você tem três ações:\n\n• "Alterar Senha" — Redefine a senha do funcionário\n• "Editar" — Abre o formulário completo de edição com cargo, nível e permissões de acesso\n• "Excluir" — Remove o funcionário após confirmação',
    position: 'center',
    dispatchEvent: 'tour-open-edit-gerenciar',
  },
  {
    selector: '.permissoes-grid',
    fallbackSelector: '.popup-content',
    title: '🔐 Permissões de Acesso',
    text: 'Aqui você pode selecionar e controlar os acessos dos funcionários. Os acessos dependem do nível do funcionário:\n\n• Supervisor, Gerente, Diretor, TI — cada nível tem um conjunto padrão de permissões.\n\nAlém disso, você pode liberar ou bloquear manualmente cada página que o funcionário terá acesso, marcando ou desmarcando a caixinha ao lado de cada módulo e depois salvando.',
    position: 'right',
    balloonWidth: 280,
    dispatchEvent: 'tour-set-nivel-gerente-gerenciar',
  },
];

// Mapa de tours por rota
const tourStepsMap = {
  '/sidebar/Home': homeTourSteps,
  '/sidebar/Cliente': clientesTourSteps,
  '/sidebar/FornecedorDropdown': fornecedoresTourSteps,
  '/sidebar/ResponsaveisDropdown': pessoasTourSteps,
  '/sidebar/AgendamentosDropdown': agendamentosTourSteps,
  '/sidebar/CalendarioDropdown': calendarioTourSteps,
  '/sidebar/OportunidadeDropdown': oportunidadesTourSteps,
  '/sidebar/OrcamentoDropdown': orcamentosTourSteps,
  '/sidebar/VendasDropdown': vendasServicosTourSteps,
  '/sidebar/VendasProdutosDropdown': vendasProdutosTourSteps,
  '/sidebar/ContratoDropdown': contratosTourSteps,
  '/sidebar/ContasPagarDropdown': contasPagarTourSteps,
  '/sidebar/ContasReceberDropdown': contasReceberTourSteps,
  '/sidebar/NotaFiscalDropdown': notaFiscalTourSteps,
  '/sidebar/CertificadoPlugNotasDropdown': certificadoTourSteps,
  '/sidebar/ProducoesDropdown': producoesTourSteps,
  '/sidebar/ProjetoVisualDropdown': projetoVisualTourSteps,
  '/sidebar/TarefasProjetoDropdown': tarefasProjetoTourSteps,
  '/sidebar/MateriaPrimaDropdown': materiaPrimaTourSteps,
  '/sidebar/ServicosTerceirosDropdown': servicosTerceirosTourSteps,
  '/sidebar/GarantiaDropdown': garantiaTourSteps,
  '/sidebar/ManutencaoDropdown': manutencaoTourSteps,
  '/sidebar/RelacionamentoClientesDropdown': relacionamentoClientesTourSteps,
  '/sidebar/EntregasProdutosDropdown': entregasProdutosTourSteps,
  '/sidebar/ProdutoDropdown': produtosTourSteps,
  '/sidebar/EstoqueMaterialDropdown': estoqueMaterialTourSteps,
  '/sidebar/CadastrarUsuario': cadastrarFuncionariosTourSteps,
  '/sidebar/GerenciarFuncionarios': gerenciarFuncionariosTourSteps,
  '/sidebar/CadastrarConta': cadastrarContaTourSteps,
};

// Resumos de formulários abertos (substitui "Resumo desta página" quando o form está aberto)
const formDescriptions = {
  '/sidebar/OrcamentoDropdown': {
    title: 'Formulário de Orçamento',
    sections: [
      {
        icon: '📋',
        title: 'Orçamento Geral',
        content:
          `Este é o primeiro bloco do formulário — onde você define as informações principais do orçamento.\n\n` +
          `• Cliente — O cliente para quem o orçamento está sendo feito (preenchido automaticamente ao selecionar no passo anterior).\n` +
          `• Oportunidade — A oportunidade comercial vinculada a este orçamento (preenchida automaticamente).\n` +
          `• Preço Final — Valor total calculado automaticamente somando serviços + serviços terceiros + custo de responsáveis. Pode ser ajustado manualmente.\n` +
          `• Status — Situação atual do orçamento: Pendente (aguardando aprovação), Aprovado ou Rejeitado.\n` +
          `• Data do Orçamento — Data de criação ou emissão do documento.\n` +
          `• Validade — Por quantos dias o orçamento permanece válido.\n` +
          `• Desconto — Valor de desconto a ser aplicado sobre o total.\n` +
          `• Observações — Campo livre para anotações internas ou informações para o cliente.`
      },
      {
        icon: '🔧',
        title: 'Serviços do Orçamento',
        content:
          `Aqui você lista todos os serviços que serão realizados e incluídos neste orçamento.\n\n` +
          `• Cada linha representa um serviço individual com: Descrição do serviço, Horas previstas e Valor do serviço (R$).\n` +
          `• O valor de cada serviço é somado automaticamente ao Preço Final do orçamento.\n` +
          `• Use o botão "+ Adicionar Serviço" para incluir mais linhas.\n` +
          `• Para remover um serviço, clique no botão de exclusão (×) ao lado da linha.\n` +
          `• Descreva cada serviço com clareza — esse texto pode aparecer no PDF do orçamento enviado ao cliente.`
      },
      {
        icon: '💰',
        title: 'Condições de Pagamento',
        content:
          `Neste bloco você define como o pagamento do orçamento será realizado.\n\n` +
          `• Cada condição representa uma parcela ou etapa de pagamento, com: Descrição (ex: "Sinal", "Na entrega"), Valor (R$) e Data de vencimento.\n` +
          `• Você pode ter múltiplas condições para pagamentos parcelados ou em etapas.\n` +
          `• Use "+ Adicionar Condição" para inserir novas linhas.\n` +
          `• O sistema valida que todas as condições possuem descrição antes de salvar.\n` +
          `• Essas condições ficam salvas no orçamento e podem ser visualizadas pelo cliente ao acessar o link de aprovação.`
      },
      {
        icon: '👥',
        title: 'Responsáveis',
        content:
          `Aqui você aloca as pessoas que irão executar este orçamento/projeto.\n\n` +
          `• Selecione o responsável pelo nome no campo dropdown.\n` +
          `• Informe as Horas Previstas para cada responsável — o custo é calculado automaticamente com base no valor/hora cadastrado na pessoa.\n` +
          `• Você pode adicionar múltiplos responsáveis ao mesmo orçamento.\n` +
          `• O custo total dos responsáveis é somado ao Preço Final automaticamente.\n` +
          `• Na edição, os responsáveis já salvos aparecem na lista "Já alocados" com opção de remover. Novos responsáveis são adicionados na seção "Adicionar novos".`
      },
      {
        icon: '🪨',
        title: 'Matérias-Primas',
        content:
          `Neste bloco você aloca os materiais e matéria-prima necessários para executar o orçamento.\n\n` +
          `• Selecione o material do estoque, informe a quantidade necessária e a data de execução prevista.\n` +
          `• O sistema verifica o estoque disponível — se a quantidade solicitada ultrapassar o disponível, será indicado como "Faltante".\n` +
          `• O valor do material é calculado com base no custo unitário cadastrado no estoque.\n` +
          `• Matérias-primas alocadas no orçamento reservam o estoque correspondente automaticamente.\n` +
          `• Você pode adicionar múltiplos itens de materiais para cobrir todos os insumos do projeto.`
      },
      {
        icon: '🤝',
        title: 'Serviços Terceiros',
        content:
          `Aqui você registra serviços que serão contratados de fornecedores externos para compor o orçamento.\n\n` +
          `• Cada serviço terceirizado tem: Descrição, Valor (R$), Fornecedor, Tipo de Serviço, Data de Execução e Status.\n` +
          `• O Fornecedor deve estar cadastrado previamente na página de Fornecedores.\n` +
          `• O Tipo de Serviço é uma classificação (ex: mão de obra, instalação, transporte).\n` +
          `• O valor dos serviços terceiros é somado ao Preço Final do orçamento.\n` +
          `• Na edição, os serviços já salvos aparecem em "Já alocados". Novos serviços são adicionados em "Adicionar novos" e só são gravados ao salvar o formulário.`
      }
    ]
  }
};

export default function AjudaWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('menu'); // 'menu' | 'resumo-pagina' | 'resumo-formulario'
  const [iaChatOpen, setIaChatOpen] = useState(false);
  const [iaChatMessages, setIaChatMessages] = useState([
    { id: 1, from: 'ia', text: 'Olá! Sou o assistente IA do S6 Gestão. Como posso te ajudar?' }
  ]);
  const [iaChatInput, setIaChatInput] = useState('');
  const [iaChatLoading, setIaChatLoading] = useState(false);
  const iaChatContextoId = useRef(null);
  const iaChatMessagesRef = useRef(null);
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [balloonPos, setBalloonPos] = useState(null);
  const [orcamentoFormularioAberto, setOrcamentoFormularioAberto] = useState(false);
  const [orcamentoOutroPopupAberto, setOrcamentoOutroPopupAberto] = useState(false);
  const [agendamentoFormularioAberto, setAgendamentoFormularioAberto] = useState(false);
  const [agendamentoOutroPopupAberto, setAgendamentoOutroPopupAberto] = useState(false);
  const [contratoFormularioAberto, setContratoFormularioAberto] = useState(false);
  const [vendasProdutosFormularioAberto, setVendasProdutosFormularioAberto] = useState(false);
  const [vendasServicosFormularioAberto, setVendasServicosFormularioAberto] = useState(false);
  const [projetoVisualFormularioAberto, setProjetoVisualFormularioAberto] = useState(false);
  const [tarefasFormularioAberto, setTarefasFormularioAberto] = useState(false);
  const [manutencaoFormularioAberto, setManutencaoFormularioAberto] = useState(false);
  const [garantiaFormularioAberto, setGarantiaFormularioAberto] = useState(false);
  const [produtoFormularioAberto, setProdutoFormularioAberto] = useState(false);
  const [materiaPrimaFormularioAberto, setMateriaPrimaFormularioAberto] = useState(false);
  const [promptEmailAberto, setPromptEmailAberto] = useState(false);
  // When form tour is running we store a separate steps array and step index
  const [formTourActive, setFormTourActive] = useState(false);
  const [formTourSteps, setFormTourSteps] = useState([]);
  const [formTourStep, setFormTourStep] = useState(0);
  const location = useLocation();

  // Listen for mobile footer open-ajuda event
  useEffect(() => {
    const handler = () => { setIsOpen(true); setView('menu'); };
    window.addEventListener('open-ajuda-widget', handler);
    return () => window.removeEventListener('open-ajuda-widget', handler);
  }, []);

  // Listen for direct IA chat open (mobile help button)
  useEffect(() => {
    const handler = () => { setIsOpen(false); setIaChatOpen(true); };
    window.addEventListener('open-ia-chat', handler);
    return () => window.removeEventListener('open-ia-chat', handler);
  }, []);

  // Listen for OrcamentoDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setOrcamentoFormularioAberto(e.detail?.formularioAberto ?? false);
      setOrcamentoOutroPopupAberto(e.detail?.outroPopupAberto ?? false);
      // When form closes, end any active form tour and return to menu
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('orcamento-popup-state', handler);
    return () => window.removeEventListener('orcamento-popup-state', handler);
  }, []);

  // Listen for AgendamentosDropdown / CalendarioDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setAgendamentoFormularioAberto(e.detail?.formularioAberto ?? false);
      setAgendamentoOutroPopupAberto(e.detail?.outroPopupAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('agendamento-popup-state', handler);
    return () => window.removeEventListener('agendamento-popup-state', handler);
  }, []);

  // Listen for ContratoDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setContratoFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('contrato-popup-state', handler);
    return () => window.removeEventListener('contrato-popup-state', handler);
  }, []);

  // Listen for VendasProdutosDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setVendasProdutosFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('vendas-produtos-popup-state', handler);
    return () => window.removeEventListener('vendas-produtos-popup-state', handler);
  }, []);

  // Listen for VendasDropdown (serviços) form state changes
  useEffect(() => {
    const handler = (e) => {
      setVendasServicosFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('vendas-servicos-popup-state', handler);
    return () => window.removeEventListener('vendas-servicos-popup-state', handler);
  }, []);

  // Listen for ProjetoVisualDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setProjetoVisualFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('projeto-visual-popup-state', handler);
    return () => window.removeEventListener('projeto-visual-popup-state', handler);
  }, []);

  // Listen for TarefasProjetoDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setTarefasFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('tarefas-popup-state', handler);
    return () => window.removeEventListener('tarefas-popup-state', handler);
  }, []);

  // Listen for ManutencaoDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setManutencaoFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('manutencao-popup-state', handler);
    return () => window.removeEventListener('manutencao-popup-state', handler);
  }, []);

  // Listen for GarantiaDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setGarantiaFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('garantia-popup-state', handler);
    return () => window.removeEventListener('garantia-popup-state', handler);
  }, []);

  // Listen for MateriaPrimaDropdown popup (resumo de uma produção) state changes
  useEffect(() => {
    const handler = (e) => {
      setMateriaPrimaFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('materia-prima-popup-state', handler);
    return () => window.removeEventListener('materia-prima-popup-state', handler);
  }, []);

  // Listen for ProdutoDropdown form state changes
  useEffect(() => {
    const handler = (e) => {
      setProdutoFormularioAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('produto-popup-state', handler);
    return () => window.removeEventListener('produto-popup-state', handler);
  }, []);

  // Listen for PromptEmailEditor (popup aberto em Relacionamento com Clientes)
  useEffect(() => {
    const handler = (e) => {
      setPromptEmailAberto(e.detail?.formularioAberto ?? false);
      if (!e.detail?.formularioAberto) {
        setFormTourActive(false);
        setHighlightRect(null);
        setBalloonPos(null);
        setView(v => v === 'resumo-formulario' ? 'menu' : v);
      }
    };
    window.addEventListener('prompt-email-popup-state', handler);
    return () => window.removeEventListener('prompt-email-popup-state', handler);
  }, []);

  const currentPath = location.pathname;
  const currentPageName = routeNames[currentPath] || currentPath.replace('/sidebar/', '').replace('Dropdown', '');
  const isHomePage = currentPath === '/sidebar/Home';
  const hasTour = !!tourStepsMap[currentPath];

  // On Orcamento page, if form is open show form summary card; otherwise show page summary card
  const isOrcamentoPage = currentPath === '/sidebar/OrcamentoDropdown';
  const isAgendamentoPage = currentPath === '/sidebar/AgendamentosDropdown' || currentPath === '/sidebar/CalendarioDropdown';
  const isClientePage = currentPath === '/sidebar/Cliente' || currentPath === '/sidebar/ClientesDropdown';
  const isFornecedorPage = currentPath === '/sidebar/FornecedorDropdown';
  const isOportunidadePage = currentPath === '/sidebar/OportunidadeDropdown';
  const isPessoasPage = currentPath === '/sidebar/ResponsaveisDropdown';
  const isContasReceberPage = currentPath === '/sidebar/ContasReceberDropdown';
  const isContratoPage = currentPath === '/sidebar/ContratoDropdown';
  const isContasPagarPage = currentPath === '/sidebar/ContasPagarDropdown';
  const isVendasProdutosPage = currentPath === '/sidebar/VendasProdutosDropdown';
  const isVendasServicosPage = currentPath === '/sidebar/VendasDropdown';
  const isProjetoVisualPage = currentPath === '/sidebar/ProjetoVisualDropdown';
  const isTarefasPage = currentPath === '/sidebar/TarefasProjetoDropdown';
  const isMateriaPrimaPage = currentPath === '/sidebar/MateriaPrimaDropdown';
  const isServicosTerceirosPage = currentPath === '/sidebar/ServicosTerceirosDropdown';
  const isRelacionamentoClientesPage = currentPath === '/sidebar/RelacionamentoClientesDropdown';
  const isManutencaoPage = currentPath === '/sidebar/ManutencaoDropdown';
  const isGarantiaPage = currentPath === '/sidebar/GarantiaDropdown';
  const isProdutoPage = currentPath === '/sidebar/ProdutoDropdown';
  const isEstoqueMaterialPage = currentPath === '/sidebar/EstoqueMaterialDropdown';
  const mostrarResumoFormulario = (isOrcamentoPage && orcamentoFormularioAberto) || (isAgendamentoPage && agendamentoFormularioAberto) || (isContratoPage && contratoFormularioAberto) || (isVendasProdutosPage && vendasProdutosFormularioAberto) || (isVendasServicosPage && vendasServicosFormularioAberto) || (isProjetoVisualPage && projetoVisualFormularioAberto) || (isTarefasPage && tarefasFormularioAberto) || (isManutencaoPage && manutencaoFormularioAberto) || (isGarantiaPage && garantiaFormularioAberto) || (isProdutoPage && produtoFormularioAberto) || (isMateriaPrimaPage && materiaPrimaFormularioAberto) || (isRelacionamentoClientesPage && promptEmailAberto);
  const mostrarResumoPagina = !mostrarResumoFormulario;

  const handleOpen = () => {
    setIsOpen(true);
    setView('menu');
  };

  const handleClose = () => {
    setIsOpen(false);
    setView('menu');
  };

  const openIaChat = () => {
    setIsOpen(false);
    setIaChatOpen(true);
  };

  const closeIaChat = () => {
    setIaChatOpen(false);
  };

  const handleIaChatSend = async () => {
    const text = iaChatInput.trim();
    if (!text || iaChatLoading) return;
    const userMsg = { id: Date.now(), from: 'user', text };
    setIaChatMessages(prev => [...prev, userMsg]);
    setIaChatInput('');
    setIaChatLoading(true);
    try {
      const token = getAuthToken();
      const bases = [
        `${BASE_URL}/ChatIA/enviar`,
        `${BASE_URL}/ChatIA/enviar`,
      ];
      const payload = {
        Mensagem: text,
        ContextoConversaId: iaChatContextoId.current || undefined,
        IncluirDocumentacaoCompleta: false,
        ContextoTela: window.location.pathname
      };
      let resposta = null;
      let erroServidor = null;
      for (const url of bases) {
        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(payload)
          });
          if (res.ok) {
            const data = await res.json();
            resposta = data;
            break;
          }
          // Guarda o motivo devolvido pelo servidor para mostrar ao usuário
          const corpo = await res.json().catch(() => null);
          erroServidor = corpo?.mensagem || corpo?.error || `Erro ${res.status} no servidor.`;
          break;
        } catch {}
      }
      if (resposta) {
        if (resposta.contextoConversaId) iaChatContextoId.current = resposta.contextoConversaId;
        setIaChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          from: 'ia',
          text: resposta.mensagemIA || resposta.MensagemIA || resposta.resposta || resposta.Resposta || 'Sem resposta.'
        }]);
      } else {
        setIaChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          from: 'ia',
          text: erroServidor || 'Não foi possível conectar ao assistente IA. Tente novamente mais tarde.'
        }]);
      }
    } catch (e) {
      setIaChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        from: 'ia',
        text: `Erro ao contatar a IA: ${e.message}`
      }]);
    } finally {
      setIaChatLoading(false);
    }
  };

  const getPageResumo = () => {
    return pageDescriptions[currentPath] || {
      title: currentPageName,
      content: `Você está na página "${currentPageName}".\n\nEsta página faz parte do sistema S6 Gestão. Use o menu lateral para navegar entre os módulos disponíveis.`
    };
  };

  // Steps do tour guiado para o formulário de orçamento (criação e edição)
  const agendamentoFormTourSteps = [
    {
      selector: '#agend-form-geral',
      title: '📅 Formulário de Agendamento',
      text: 'Aqui você preenche todos os dados do agendamento: data, cliente vinculado, responsável, status, oportunidade, tipo, título, descrição, local e prioridade. Campos marcados em vermelho são obrigatórios.',
      position: 'right',
    },
    {
      selector: '#agend-secao-horarios',
      title: '⏰ Horários',
      text: 'Clique em um horário para definir o INÍCIO e depois outro para o FIM. Horários em vermelho já estão ocupados por outros agendamentos. Em verde aparecem os horários do agendamento atual (modo edição). Salve a data primeiro para carregar os horários do dia.',
      position: 'right',
    },
    {
      selector: '#agend-secao-tipo',
      title: '🏷️ Tipo de Agendamento',
      text: 'Selecione o tipo que define a finalidade: Oportunidade, Entrega, Garantia ou Manutenção. Ao escolher, será solicitado o item correspondente para vincular ao agendamento.',
      position: 'right',
    },
  ];

  const orcamentoFormTourSteps = [
    {
      selector: '#orc-secao-geral',
      title: '📋 Orçamento Geral',
      text: 'Aqui ficam as informações principais: cliente, oportunidade vinculada, preço final (calculado automaticamente), status, data, validade, desconto e observações.',
      position: 'right',
    },
    {
      selector: '#orc-secao-servicos',
      title: '🔧 Serviços do Orçamento',
      text: 'Liste aqui todos os serviços que compõem este orçamento. Para cada serviço informe a descrição, horas previstas e valor. O total é somado automaticamente ao Preço Final.',
      position: 'right',
    },
    {
      selector: '#orc-secao-condicoes',
      title: '💰 Condições de Pagamento',
      text: 'Defina como o pagamento será feito: adicione uma ou mais condições com descrição (ex: "Sinal"), valor (R$) e data de vencimento. Aparece na aprovação enviada ao cliente.',
      position: 'right',
    },
    {
      selector: '#orc-secao-responsaveis',
      title: '👥 Responsáveis',
      text: 'Aloque as pessoas que executarão este projeto. Selecione o responsável e informe as Horas Previstas — o custo é calculado pelo valor/hora cadastrado e somado ao Preço Final.',
      position: 'right',
    },
    {
      selector: '#orc-secao-materias',
      title: '🪨 Matérias-Primas',
      text: 'Aloque os materiais do estoque necessários para o projeto. O sistema verifica o estoque disponível e alerta quando a quantidade solicitada ultrapassa o estoque.',
      position: 'right',
    },
    {
      selector: '#orc-secao-servicos-terceiros',
      title: '🤝 Serviços Terceiros',
      text: 'Registre serviços contratados de fornecedores externos: descrição, valor, fornecedor, tipo, data de execução e status. O valor é somado ao Preço Final do orçamento.',
      position: 'right',
    },
  ];

  // Inicia o tour do formulário (orçamento, agendamento ou contrato conforme a página atual)
  const startFormTour = () => {
    setIsOpen(false);
    const steps = (isRelacionamentoClientesPage && promptEmailAberto) ? promptEmailTourSteps
      // Pop-up da Matéria-Prima: só as partes que existem nele (o de orçamento sem produção só tem as reservadas)
      : isMateriaPrimaPage ? materiaPrimaFormTourSteps.filter(s => document.querySelector(s.selector))
      : isAgendamentoPage ? agendamentoFormTourSteps
      // Aditivos só aparecem em contrato assinado
      : isContratoPage ? contratoFormTourSteps.filter(s => s.selector !== '#contrato-secao-aditivos' || document.querySelector(s.selector))
      : isVendasProdutosPage ? vendasProdutosFormTourSteps
      : isVendasServicosPage ? vendasServicosFormTourSteps
      : isProjetoVisualPage ? projetoVisualFormTourSteps
      : isTarefasPage ? tarefasFormTourSteps
      : isManutencaoPage ? manutencaoFormTourSteps
      : isGarantiaPage ? garantiaFormTourSteps
      : isProdutoPage ? produtoFormTourSteps
      : orcamentoFormTourSteps;
    setFormTourSteps(steps);
    setFormTourStep(0);
    setFormTourActive(true);
    goToFormTourStep(0, steps);
  };

  // Finaliza o tour do formulário
  const endFormTour = useCallback(() => {
    setFormTourActive(false);
    setFormTourStep(0);
    setHighlightRect(null);
    setBalloonPos(null);
    document.querySelectorAll('.tour-highlighted').forEach(el => el.classList.remove('tour-highlighted'));
    window.dispatchEvent(new CustomEvent('prompt-email-tour-acao', { detail: 'voltar-blocos' }));
  }, []);
  const calculateBalloonPosition = useCallback((rect, position, customWidth) => {
    if (!rect) return null;

    const padding = 16;
    const balloonWidth = customWidth || 360;
    const balloonApproxHeight = 280;
    const minTop = 64 + padding;
    const maxTop = window.innerHeight - balloonApproxHeight - padding;
    const maxLeft = window.innerWidth - balloonWidth - padding;

    const clampLeft = (l) => Math.max(padding, Math.min(maxLeft, l));
    const clampTop = (t) => Math.max(minTop, Math.min(maxTop, t));

    // Auto-flip when not enough space
    let pos = position;
    if (pos === 'bottom' && rect.bottom + balloonApproxHeight + padding > window.innerHeight) {
      pos = 'top';
    } else if (pos === 'top' && rect.top - balloonApproxHeight - padding < minTop) {
      pos = 'bottom';
    }
    if (pos === 'right' && rect.right + balloonWidth + padding > window.innerWidth) {
      pos = 'left';
    } else if (pos === 'left' && rect.left - balloonWidth - padding < padding) {
      pos = 'right';
    }

    switch (pos) {
      case 'inner-top':
        return {
          top: clampTop(rect.top + 16),
          left: clampLeft(rect.left + (rect.width / 2) - (balloonWidth / 2)),
          arrow: null,
        };
      case 'center':
        return {
          top: clampTop(rect.top + Math.min(rect.height / 2, 80)),
          left: clampLeft(rect.left + (rect.width / 2) - (balloonWidth / 2)),
          arrow: null,
        };
      case 'bottom':
        return {
          top: clampTop(rect.bottom + padding),
          left: clampLeft(rect.left + (rect.width / 2) - (balloonWidth / 2)),
          arrow: 'top',
        };
      case 'top': {
        const topVal = rect.top - balloonApproxHeight - padding;
        return {
          top: clampTop(topVal),
          left: clampLeft(rect.left + (rect.width / 2) - (balloonWidth / 2)),
          arrow: 'bottom',
        };
      }
      case 'right':
        return {
          top: clampTop(rect.top),
          left: clampLeft(rect.right + padding),
          arrow: 'left',
        };
      case 'left':
        return {
          top: clampTop(rect.top),
          left: clampLeft(rect.left - balloonWidth - padding),
          arrow: 'right',
        };
      default:
        return {
          top: clampTop(rect.bottom + padding),
          left: clampLeft(rect.left),
          arrow: 'top',
        };
    }
  }, []);

  // Ir para o step do tour
  const goToTourStep = useCallback((stepIdx) => {
    const steps = tourStepsMap[currentPath] || homeTourSteps;
    if (stepIdx >= steps.length) {
      // Tour finalizado
      setTourActive(false);
      setTourStep(0);
      setHighlightRect(null);
      setBalloonPos(null);
      // Remove classe de destaque
      document.querySelectorAll('.tour-highlighted').forEach(el => el.classList.remove('tour-highlighted'));
      // Fecha formulários abertos pelo tour
      window.dispatchEvent(new CustomEvent('fechar-formulario-agendamento-tour'));
      return;
    }

    const step = steps[stepIdx];

    // Se precisa expandir o composer
    if (step.expandComposer) {
      const composerInput = document.querySelector('.feed-card .composer input, .feed-card .composer textarea, .feed-card .input-row input');
      if (composerInput) {
        composerInput.focus();
        composerInput.click();
      }
    }

    // Se precisa disparar um evento customizado (ex: abrir formulário)
    if (step.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(step.dispatchEvent));
    }

    // Pequeno delay para garantir que o DOM atualizou (ex: composer expandiu)
    setTimeout(() => {
      // Remove destaque anterior
      document.querySelectorAll('.tour-highlighted').forEach(el => el.classList.remove('tour-highlighted'));

      // Encontra o elemento
      let el = document.querySelector(step.selector);
      if (!el && step.fallbackSelector) {
        el = document.querySelector(step.fallbackSelector);
      }

      if (el) {
        // Scroll suave até o elemento (exceto se for fixed)
        if (!step.isFixed) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        setTimeout(() => {
          el.classList.add('tour-highlighted');
          const rect = el.getBoundingClientRect();
          setHighlightRect({
            x: rect.left - 6,
            y: rect.top - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          });
          setBalloonPos(calculateBalloonPosition(rect, step.position, step.balloonWidth));
          setTourStep(stepIdx);
        }, 350);
      } else {
        // Se não encontrou, pula para o próximo
        setTourStep(stepIdx);
        setHighlightRect(null);
        setBalloonPos({
          top: window.innerHeight / 2 - 100,
          left: window.innerWidth / 2 - 180,
          arrow: null,
        });
      }
    }, step.expandComposer || step.dispatchEvent ? 600 : 100);
  }, [calculateBalloonPosition, currentPath]);

  // Inicia o tour
  const startTour = () => {
    setIsOpen(false);
    setTourActive(true);
    setTourStep(0);
    goToTourStep(0);
  };

  // --- Tour do formulário (usa o mesmo engine de highlight/balão mas steps separados) ---
  const goToFormTourStep = useCallback((stepIdx, steps) => {
    const stepsArr = steps || formTourSteps;
    if (stepIdx >= stepsArr.length) {
      setFormTourActive(false);
      setFormTourStep(0);
      setHighlightRect(null);
      setBalloonPos(null);
      document.querySelectorAll('.tour-highlighted').forEach(el => el.classList.remove('tour-highlighted'));
      window.dispatchEvent(new CustomEvent('prompt-email-tour-acao', { detail: 'voltar-blocos' }));
      return;
    }
    const step = stepsArr[stepIdx];
    // Passo que precisa abrir um painel antes (ex.: editor de Prompt E-mail)
    if (step.acaoPromptEmail) {
      window.dispatchEvent(new CustomEvent('prompt-email-tour-acao', { detail: step.acaoPromptEmail }));
    }
    setTimeout(() => {
      document.querySelectorAll('.tour-highlighted').forEach(el => el.classList.remove('tour-highlighted'));
      let el = document.querySelector(step.selector);
      if (!el && step.fallbackSelector) el = document.querySelector(step.fallbackSelector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          el.classList.add('tour-highlighted');
          const rect = el.getBoundingClientRect();
          setHighlightRect({
            x: rect.left - 6,
            y: rect.top - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          });
          setBalloonPos(calculateBalloonPosition(rect, step.position, step.balloonWidth));
          setFormTourStep(stepIdx);
        }, 350);
      } else {
        setFormTourStep(stepIdx);
        setHighlightRect(null);
        setBalloonPos({
          top: window.innerHeight / 2 - 100,
          left: window.innerWidth / 2 - 180,
          arrow: null,
        });
      }
    }, step.acaoPromptEmail ? 350 : 100);
  }, [calculateBalloonPosition, formTourSteps]);

  // Finaliza o tour
  const endTour = useCallback(() => {
    setTourActive(false);
    setTourStep(0);
    setHighlightRect(null);
    setBalloonPos(null);
    document.querySelectorAll('.tour-highlighted').forEach(el => el.classList.remove('tour-highlighted'));
    // Fecha formulários abertos pelo tour
    window.dispatchEvent(new CustomEvent('fechar-formulario-agendamento-tour'));
  }, []);

  // Limpa tour ao mudar de página
  useEffect(() => {
    if (tourActive) endTour();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  // Bloqueia scroll e interação do body enquanto o tour estiver ativo
  useEffect(() => {
    if (!tourActive) return;

    // Bloqueia overflow do body
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    // Bloqueia wheel e touchmove com passive: false para poder fazer preventDefault
    const blockScroll = (e) => e.preventDefault();
    window.addEventListener('wheel', blockScroll, { passive: false });
    window.addEventListener('touchmove', blockScroll, { passive: false });

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
      window.removeEventListener('wheel', blockScroll);
      window.removeEventListener('touchmove', blockScroll);
    };
  }, [tourActive]);

  // Recalcula posição no resize
  useEffect(() => {
    if (!tourActive) return;

    const handleResize = () => {
      goToTourStep(tourStep);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tourActive, tourStep, goToTourStep]);

  // Bloqueia scroll durante o tour do formulário
  useEffect(() => {
    if (!formTourActive) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const blockScroll = (e) => e.preventDefault();
    window.addEventListener('wheel', blockScroll, { passive: false });
    window.addEventListener('touchmove', blockScroll, { passive: false });
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('wheel', blockScroll);
      window.removeEventListener('touchmove', blockScroll);
    };
  }, [formTourActive]);

  // Recalcula posição no resize durante o tour do formulário
  useEffect(() => {
    if (!formTourActive) return;
    const handleResize = () => goToFormTourStep(formTourStep);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [formTourActive, formTourStep, goToFormTourStep]);

  const activeTourSteps = tourStepsMap[currentPath] || homeTourSteps;
  const currentTourStep = activeTourSteps[tourStep];
  const isLastStep = tourStep === activeTourSteps.length - 1;

  const currentFormTourStep = formTourSteps[formTourStep];
  const isLastFormStep = formTourStep === formTourSteps.length - 1;

  return (
    <>
      {/* Botão flutuante */}
      {!isOpen && !tourActive && !formTourActive && !iaChatOpen && (
        <div className={`ajuda-widget-icon${currentPath === '/personalizar-contrato' ? ' ajuda-widget-icon--ao-lado-salvar' : ''}`} onClick={handleOpen} title="Ajuda">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Ícone de headset/fone com microfone */}
            <path d="M12 1C7.03 1 3 5.03 3 10v5c0 1.66 1.34 3 3 3h1v-7H5.5v-1c0-3.59 2.91-6.5 6.5-6.5s6.5 2.91 6.5 6.5v1H17v7h1c1.66 0 3-1.34 3-3v-5c0-4.97-4.03-9-9-9z" fill="white"/>
            <path d="M5 12v5h2v-5H5zM17 12v5h2v-5h-2z" fill="white"/>
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z" fill="white"/>
            <path d="M14 20h-4v-1h4v1z" fill="white"/>
          </svg>
        </div>
      )}

      {/* Overlay + Sidebar */}
      {isOpen && (
        <>
          <div className="ajuda-sidebar-overlay" onClick={handleClose} />
          <div className="ajuda-sidebar">
            {/* Header */}
            <div className="ajuda-sidebar-header">
              <h3>
                {view !== 'menu' && (
                  <button className="ajuda-sidebar-back-btn" onClick={() => setView('menu')} title="Voltar">
                    ←
                  </button>
                )}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 1C7.03 1 3 5.03 3 10v5c0 1.66 1.34 3 3 3h1v-7H5.5v-1c0-3.59 2.91-6.5 6.5-6.5s6.5 2.91 6.5 6.5v1H17v7h1c1.66 0 3-1.34 3-3v-5c0-4.97-4.03-9-9-9z" fill="white"/>
                  <path d="M5 12v5h2v-5H5zM17 12v5h2v-5h-2z" fill="white"/>
                </svg>
                Ajuda
              </h3>
              <button className="ajuda-sidebar-close" onClick={handleClose}>✕</button>
            </div>

            {/* Conteúdo */}
            <div className="ajuda-sidebar-content">
              {view === 'menu' && (
                <>
                  {/* Resumo desta página — só aparece quando NÃO há formulário aberto (ou não é a página de orçamento) */}
                  {mostrarResumoPagina && (
                    <div className="ajuda-menu-item" onClick={() => {
                      // Se estiver na página de orçamento e houver popups não-formulário abertos, fecha tudo primeiro
                      if (isOrcamentoPage && orcamentoOutroPopupAberto) {
                        window.dispatchEvent(new CustomEvent('orcamento-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de agendamento e houver popups não-formulário abertos, fecha tudo primeiro
                      if (isAgendamentoPage && agendamentoOutroPopupAberto) {
                        window.dispatchEvent(new CustomEvent('agendamento-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de clientes, fecha todos os popups de cliente antes de mostrar o resumo da página
                      if (isClientePage) {
                        window.dispatchEvent(new CustomEvent('clientes-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de fornecedores, fecha todos os popups de fornecedor antes de mostrar o resumo da página
                      if (isFornecedorPage) {
                        window.dispatchEvent(new CustomEvent('fornecedores-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de oportunidades, fecha todos os popups de oportunidades antes de mostrar o resumo da página
                      if (isOportunidadePage) {
                        window.dispatchEvent(new CustomEvent('oportunidades-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de pessoas, fecha todos os popups de pessoas antes de mostrar o resumo da página
                      if (isPessoasPage) {
                        window.dispatchEvent(new CustomEvent('pessoas-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de contas a receber, fecha todos os popups antes de mostrar o resumo
                      if (isContasReceberPage) {
                        window.dispatchEvent(new CustomEvent('contasreceber-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de contratos, fecha todos os popups antes de mostrar o resumo
                      if (isContratoPage) {
                        window.dispatchEvent(new CustomEvent('contrato-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de contas a pagar, fecha todos os popups antes de mostrar o resumo
                      if (isContasPagarPage) {
                        window.dispatchEvent(new CustomEvent('contaspagar-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de vendas de produtos, fecha todos os popups antes de mostrar o resumo
                      if (isVendasProdutosPage) {
                        window.dispatchEvent(new CustomEvent('vendas-produtos-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de vendas de serviços, fecha todos os popups antes de mostrar o resumo
                      if (isVendasServicosPage) {
                        window.dispatchEvent(new CustomEvent('vendas-servicos-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      // Se estiver na página de projeto visual, fecha todos os popups antes de mostrar o resumo
                      if (isProjetoVisualPage) {
                        window.dispatchEvent(new CustomEvent('projeto-visual-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isTarefasPage) {
                        window.dispatchEvent(new CustomEvent('tarefas-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isMateriaPrimaPage) {
                        window.dispatchEvent(new CustomEvent('materia-prima-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isEstoqueMaterialPage) {
                        window.dispatchEvent(new CustomEvent('estoque-material-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isServicosTerceirosPage) {
                        window.dispatchEvent(new CustomEvent('servicos-terceiros-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isRelacionamentoClientesPage) {
                        window.dispatchEvent(new CustomEvent('relacionamento-clientes-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isManutencaoPage) {
                        window.dispatchEvent(new CustomEvent('manutencao-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isGarantiaPage) {
                        window.dispatchEvent(new CustomEvent('garantia-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (isProdutoPage) {
                        window.dispatchEvent(new CustomEvent('produto-fechar-todos-popups'));
                        setTimeout(() => {
                          if (hasTour) startTour();
                          else setView('resumo-pagina');
                        }, 150);
                        return;
                      }
                      if (hasTour) {
                        startTour();
                      } else {
                        setView('resumo-pagina');
                      }
                    }}>
                      <div className="ajuda-menu-item-icon pagina">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 9h-2v2H9v-2H7v-2h2V7h2v2h2v2zm-1-8.5L17.5 8H12V2.5z" fill="white"/>
                        </svg>
                      </div>
                      <div className="ajuda-menu-item-text">
                        <h4>Resumo desta página</h4>
                        <p>Veja o que você pode fazer em "{currentPageName}"</p>
                      </div>
                    </div>
                  )}

                  {/* Resumo deste formulário — só aparece quando o formulário do orçamento está aberto */}
                  {mostrarResumoFormulario && (
                    <div className="ajuda-menu-item" onClick={startFormTour}>
                      <div className="ajuda-menu-item-icon pagina" style={{ background: 'linear-gradient(135deg, rgb(235,133,0), rgb(208,116,0))' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm6 8h-4v-2h4v2zm0-4h-4v-2h4v2zm-1-5V3.5L18.5 9H14z" fill="white"/>
                        </svg>
                      </div>
                      <div className="ajuda-menu-item-text">
                        <h4>Resumo deste formulário</h4>
                        <p>Tour guiado por cada seção do formulário</p>
                      </div>
                    </div>
                  )}

                  {/* Iniciar chat com IA */}
                  <div className="ajuda-menu-item" onClick={openIaChat}>
                    <div className="ajuda-menu-item-icon ia">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-4h2v2h-2v-2zm1-10c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" fill="white"/>
                      </svg>
                    </div>
                    <div className="ajuda-menu-item-text">
                      <h4>Iniciar chat com IA</h4>
                      <p>Converse com o assistente inteligente</p>
                    </div>
                  </div>
                </>
              )}

              {view === 'resumo-pagina' && (
                <div className="ajuda-resumo-container">
                  <button className="ajuda-resumo-back" onClick={() => setView('menu')}>
                    ← Voltar
                  </button>
                  <h3 className="ajuda-resumo-title">{getPageResumo().title}</h3>
                  <div className="ajuda-resumo-body">{getPageResumo().content}</div>
                </div>
              )}

              {view === 'resumo-formulario' && (() => {
                const formDesc = formDescriptions[currentPath];
                if (!formDesc) return null;
                return (
                  <div className="ajuda-resumo-container">
                    <button className="ajuda-resumo-back" onClick={() => setView('menu')}>
                      ← Voltar
                    </button>
                    <h3 className="ajuda-resumo-title">{formDesc.title}</h3>
                    {formDesc.sections.map((sec, i) => (
                      <div key={i} style={{ marginBottom: 18 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 6,
                          paddingBottom: 4,
                          borderBottom: '2px solid rgb(235,133,0)'
                        }}>
                          <span style={{ fontSize: 18 }}>{sec.icon}</span>
                          <strong style={{ color: 'rgb(235,133,0)', fontSize: 14 }}>{sec.title}</strong>
                        </div>
                        <div className="ajuda-resumo-body" style={{ marginBottom: 0 }}>{sec.content}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>
          </div>
        </>
      )}

      {/* === CHAT COM IA POPUP === */}
      {iaChatOpen && (
        <div className="ia-chat-popup">
          <div className="ia-chat-header">
            <div className="ia-chat-header-info">
              <div className="ia-chat-avatar">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-4h2v2h-2v-2zm1-10c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" fill="white"/>
                </svg>
              </div>
              <span className="ia-chat-title">Assistente IA</span>
            </div>
            <button className="ia-chat-close" onClick={closeIaChat}>✕</button>
          </div>
          <div className="ia-chat-messages" ref={el => { iaChatMessagesRef.current = el; if (el) el.scrollTop = el.scrollHeight; }}>
            {iaChatMessages.map(msg => (
              <div key={msg.id} className={`ia-chat-msg ${msg.from === 'user' ? 'mine' : 'other'}`}>
                <div className={`ia-chat-bubble ${msg.from === 'user' ? 'mine' : 'other'}`}>
                  {msg.from === 'user' ? msg.text : <TextoFormatadoIA texto={msg.text} />}
                </div>
              </div>
            ))}
            {iaChatLoading && (
              <div className="ia-chat-msg other">
                <div className="ia-chat-bubble other" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ animation: 'ia-dot 1s infinite 0s', opacity: 0.4, fontSize: 20, lineHeight: 1 }}>●</span>
                  <span style={{ animation: 'ia-dot 1s infinite 0.3s', opacity: 0.4, fontSize: 20, lineHeight: 1 }}>●</span>
                  <span style={{ animation: 'ia-dot 1s infinite 0.6s', opacity: 0.4, fontSize: 20, lineHeight: 1 }}>●</span>
                </div>
              </div>
            )}
          </div>
          <div className="ia-chat-input-area">
            <input
              className="ia-chat-input"
              type="text"
              placeholder="Digite sua mensagem..."
              value={iaChatInput}
              onChange={e => setIaChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !iaChatLoading) handleIaChatSend(); }}
              disabled={iaChatLoading}
            />
            <button className="ia-chat-send-btn" onClick={handleIaChatSend} disabled={iaChatLoading} style={{ opacity: iaChatLoading ? 0.5 : 1 }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="white"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* === GUIDED TOUR === */}
      {tourActive && (
        <>
          {/* SVG overlay com buraco no elemento destacado */}
          <svg className="tour-highlight-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <mask id="tour-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {highlightRect && (
                  <rect
                    x={highlightRect.x}
                    y={highlightRect.y}
                    width={highlightRect.width}
                    height={highlightRect.height}
                    rx="10"
                    ry="10"
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.55)"
              mask="url(#tour-mask)"
            />
          </svg>

          {/* Bloqueador de cliques: impede que cliques fora do balão atinjam o conteúdo abaixo */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999982,
              cursor: 'default',
            }}
            onClick={e => e.stopPropagation()}
          />

          {/* Balão de explicação */}}
          {balloonPos && currentTourStep && (
            <div
              className="tour-balloon"
              style={{
                ...(balloonPos.top !== undefined ? { top: balloonPos.top } : {}),
                ...(balloonPos.bottom !== undefined ? { bottom: balloonPos.bottom } : {}),
                left: balloonPos.left,
                maxWidth: currentTourStep.balloonWidth || 380,
                ...(currentTourStep.balloonMinHeight ? { minHeight: currentTourStep.balloonMinHeight } : {}),
              }}
            >
              {balloonPos.arrow && (
                <div className={`tour-balloon-arrow ${balloonPos.arrow}`} />
              )}
              <div className="tour-balloon-title">{currentTourStep.title}</div>
              <div className="tour-balloon-text">{currentTourStep.text}</div>
              <div className="tour-balloon-footer">
                <span className="tour-balloon-steps">
                  {tourStep + 1} de {activeTourSteps.length}
                </span>
                <div className="tour-balloon-actions">
                  <button className="tour-btn-skip" onClick={endTour}>
                    Pular
                  </button>
                  {isLastStep ? (
                    <button className="tour-btn-finish" onClick={endTour}>
                      Finalizar
                    </button>
                  ) : (
                    <button className="tour-btn-next" onClick={() => goToTourStep(tourStep + 1)}>
                      Próximo →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* === TOUR DO FORMULÁRIO DE ORÇAMENTO === */}
      {formTourActive && (
        <>
          <svg className="tour-highlight-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <mask id="tour-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {highlightRect && (
                  <rect
                    x={highlightRect.x}
                    y={highlightRect.y}
                    width={highlightRect.width}
                    height={highlightRect.height}
                    rx="10"
                    ry="10"
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#tour-mask)" />
          </svg>

          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999982, cursor: 'default' }}
            onClick={e => e.stopPropagation()}
          />

          {balloonPos && currentFormTourStep && (
            <div
              className="tour-balloon"
              style={{
                ...(balloonPos.top !== undefined ? { top: balloonPos.top } : {}),
                ...(balloonPos.bottom !== undefined ? { bottom: balloonPos.bottom } : {}),
                left: balloonPos.left,
                maxWidth: 400,
              }}
            >
              {balloonPos.arrow && (
                <div className={`tour-balloon-arrow ${balloonPos.arrow}`} />
              )}
              <div className="tour-balloon-title">{currentFormTourStep.title}</div>
              <div className="tour-balloon-text">{currentFormTourStep.text}</div>
              <div className="tour-balloon-footer">
                <span className="tour-balloon-steps">
                  {formTourStep + 1} de {formTourSteps.length}
                </span>
                <div className="tour-balloon-actions">
                  <button className="tour-btn-skip" onClick={endFormTour}>
                    Pular
                  </button>
                  {formTourStep > 0 && (
                    <button className="tour-btn-next" style={{ marginRight: 4 }} onClick={() => goToFormTourStep(formTourStep - 1)}>
                      ← Anterior
                    </button>
                  )}
                  {isLastFormStep ? (
                    <button className="tour-btn-finish" onClick={endFormTour}>
                      Finalizar
                    </button>
                  ) : (
                    <button className="tour-btn-next" onClick={() => goToFormTourStep(formTourStep + 1)}>
                      Próximo →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
